// The mindmap editor: an infinite canvas (pan, zoom, pinch) that draws the map
// with engine/render.js, and every way to change it: keyboard (Tab child, Enter
// sibling, typing edits, arrows move), mouse (drag a topic onto another to
// reparent it, between two to reorder, onto empty canvas to float it), the
// context toolbar, right-click menus, relationships, find, focus on a branch,
// the outline panel, copy and paste (outlines from anywhere become branches).
//
// The editor never saves by itself: every change calls opts.onChange(); the
// host (web/app.js) writes the file. Undo keeps whole snapshots (maps are small).
import * as M from './model.js';
import { layoutMap, badgePos, measureText } from './layout.js';
import { step, cool, ALPHA_MIN, graphOpts, FORCE_DEFAULTS } from './graph.js';
import { contentMarkup, arrowDefs, esc } from './render.js';
import { THEMES, COLORS, colorHex, themeOf, FONT } from './theme.js';
import { outlineToBranches, mapToText } from './io.js';
import { h, icon, menu, toast } from '../web/ui.js';

const LAYOUT_LABEL = { balanced: 'Mind map (both sides)', right: 'Mind map (right)', left: 'Mind map (left)', down: 'Org chart (down)', up: 'Org chart (up)', tree: 'Tree (indented list)', bubble: 'Bubble map (graph)' };
const LINE_LABEL = { curved: 'Curved', straight: 'Straight', elbow: 'Elbow', rounded: 'Rounded elbow' };
const SHAPE_LABEL = { rounded: 'Rounded', pill: 'Pill', rect: 'Rectangle', ellipse: 'Ellipse', underline: 'Underline', none: 'Text only' };
const FILL_LABEL = { solid: 'Solid', soft: 'Soft', outline: 'Outline' };
const SIZE_LABEL = { s: 'Small', m: 'Medium', l: 'Large', xl: 'Extra large' };
const SPACING_LABEL = { compact: 'Compact', normal: 'Normal', roomy: 'Roomy' };
const MOD = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? 'Cmd' : 'Ctrl';

// little pictures of each layout and line style for the pickers
const glyph = (d) => `<svg viewBox="0 0 32 22" width="32" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${d}</svg>`;
const LAYOUT_GLYPH = {
  balanced: glyph('<rect x="12" y="8" width="8" height="6" rx="2"/><path d="M20 11C24 11 23 5 27 5M20 11C24 11 23 17 27 17M12 11C8 11 9 5 5 5M12 11C8 11 9 17 5 17"/>'),
  right: glyph('<rect x="3" y="8" width="8" height="6" rx="2"/><path d="M11 11C16 11 15 4 21 4M11 11H21M11 11C16 11 15 18 21 18"/><path d="M23 4h6M23 11h6M23 18h6"/>'),
  left: glyph('<rect x="21" y="8" width="8" height="6" rx="2"/><path d="M21 11C16 11 17 4 11 4M21 11H11M21 11C16 11 17 18 11 18"/><path d="M3 4h6M3 11h6M3 18h6"/>'),
  down: glyph('<rect x="12" y="2" width="8" height="6" rx="2"/><path d="M16 8V11M6 14V11H26V14M16 11V14"/><rect x="3" y="14" width="6" height="5" rx="1.5"/><rect x="13" y="14" width="6" height="5" rx="1.5"/><rect x="23" y="14" width="6" height="5" rx="1.5"/>'),
  up: glyph('<rect x="12" y="14" width="8" height="6" rx="2"/><path d="M16 14V11M6 8V11H26V8M16 11V8"/><rect x="3" y="3" width="6" height="5" rx="1.5"/><rect x="13" y="3" width="6" height="5" rx="1.5"/><rect x="23" y="3" width="6" height="5" rx="1.5"/>'),
  tree: glyph('<rect x="3" y="2" width="10" height="5" rx="1.5"/><path d="M7 7V18H12M7 12H12"/><path d="M14 12h14M14 18h10"/>'),
  bubble: glyph('<circle cx="15" cy="11" r="4.5"/><circle cx="5" cy="5" r="2.5"/><circle cx="27" cy="6" r="3"/><circle cx="7" cy="18" r="2.5"/><circle cx="25" cy="18" r="2"/><path d="M7.2 6.3L11.2 8.7M24 7.3L19.3 9.6M9 16.8L11.6 14M23.4 16.9L19 13.4"/>'),
};
const LINE_GLYPH = {
  curved: glyph('<path d="M4 17C16 17 16 5 28 5"/>'), straight: glyph('<path d="M4 17L28 5"/>'),
  elbow: glyph('<path d="M4 17H16V5H28"/>'), rounded: glyph('<path d="M4 17H12Q16 17 16 13V9Q16 5 20 5H28"/>'),
};
const SHAPE_GLYPH = {
  rounded: glyph('<rect x="4" y="5" width="24" height="12" rx="4"/>'), pill: glyph('<rect x="4" y="5" width="24" height="12" rx="6"/>'),
  rect: glyph('<rect x="4" y="5" width="24" height="12" rx="0.5"/>'), ellipse: glyph('<ellipse cx="16" cy="11" rx="12" ry="7"/>'),
  underline: glyph('<path d="M4 17H28"/><path d="M8 11H24" opacity="0.5"/>'), none: glyph('<path d="M8 11H24" opacity="0.5"/>'),
};

/**
 * opts: { host, getSettings(), setSettings(patch), onChange(), embedded,
 *         editNote(text) -> Promise<string|null>, editLink(url) -> Promise<string|null>,
 *         editLabel(text) -> Promise<string|null>, onShortcuts() }
 */
export function createMindmap(opts) {
  const S = () => opts.getSettings();
  let map = M.blankMap();
  let idx = M.indexMap(map);
  let L = null;
  let view = { x: 0, y: 0, k: 1 };
  let sel = [];               // selected topic ids (last = primary)
  let selRel = null;
  let hover = null;
  let editing = null;         // { id, ta, isNew, before, orig }
  let focusId = null;
  let locked = false;
  let ver = 0; let savedVer = 0;
  const undoStack = []; const redoStack = [];
  let drag = null;            // pointer gesture in progress
  let connectFrom = null;     // relationship being drawn from this topic
  let pointer = { x: 0, y: 0 };
  let engaged = !opts.embedded;
  let findState = null;       // { q, hits, i }
  let anim = 0;
  let destroyed = false;
  let outlineOpen = false;
  let local = null;           // bubble map: { id, depth } shows only the topics that near one topic
  // the bubble map's live physics: positions by topic id, and how hot it still is
  const sim = { pos: new Map(), arr: [], alpha: 0, raf: 0, dragId: null, near: new Set(), persistT: 0 };
  const isBubble = () => map.layout === 'bubble';

  // ---- DOM ----------------------------------------------------------------------
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('class', 'mm-svg');
  const defs = document.createElementNS(svg.namespaceURI, 'g');
  const vp = document.createElementNS(svg.namespaceURI, 'g');
  const content = document.createElementNS(svg.namespaceURI, 'g');
  const ui = document.createElementNS(svg.namespaceURI, 'g');
  ui.setAttribute('class', 'mm-ui');
  vp.append(content, ui);
  svg.append(defs, vp);
  const canvas = h('div', { class: 'mm-canvas', tabindex: '0', role: 'application', 'aria-label': 'Mindmap canvas. Tab adds a child topic, Enter a sibling, arrows move between topics, typing edits.' }, svg);
  const ta = h('textarea', { class: 'mm-edit', spellcheck: 'true', rows: '1', 'aria-label': 'Topic text', hidden: true });
  const bar = h('div', { class: 'mm-bar', role: 'toolbar', 'aria-label': 'Topic tools' });
  const zoomBox = h('div', { class: 'mm-zoom' });
  const mini = h('canvas', { class: 'mm-mini', width: '176', height: '120', 'aria-hidden': 'true' });
  const crumbs = h('div', { class: 'mm-crumbs', hidden: true });
  const findBar = h('div', { class: 'mm-find', hidden: true });
  const hint = h('div', { class: 'mm-hint', hidden: true });
  const outline = h('aside', { class: 'mm-outline', 'aria-label': 'Outline', hidden: true });
  const stage = h('div', { class: 'mm-stage' }, canvas, ta, crumbs, findBar, hint, zoomBox, mini, bar);
  const wrap = h('div', { class: 'mm-wrap' }, stage, outline);
  opts.host.replaceChildren(wrap);

  // ---- snapshots and changes ----------------------------------------------------------
  const snap = () => JSON.stringify({ map, sel, focusId });
  function restore(s) {
    const o = JSON.parse(s);
    map = M.normalize(o.map);
    focusId = o.focusId || null;
    idx = M.indexMap(map);
    sel = (o.sel || []).filter((id) => idx.has(id));
    selRel = null;
    sim.pos.clear(); // undo puts bubbles back where they were saved
  }
  /** Run an edit with undo. `fn` returns false to say nothing changed. */
  function commit(fn, { animate = true } = {}) {
    if (editing) finishEdit();
    const before = snap();
    const res = fn();
    if (res === false) return false;
    undoStack.push(before);
    if (undoStack.length > 300) undoStack.shift();
    redoStack.length = 0;
    changed(animate);
    return res;
  }
  function changed(animate = true) {
    ver++;
    relayout(animate);
    opts.onChange?.();
  }
  function undo() {
    if (editing) finishEdit();
    if (!undoStack.length) { toast('Nothing to undo.', 1400); return; }
    redoStack.push(snap());
    restore(undoStack.pop());
    changed();
  }
  function redo() {
    if (editing) finishEdit();
    if (!redoStack.length) { toast('Nothing to redo.', 1400); return; }
    undoStack.push(snap());
    restore(redoStack.pop());
    changed();
  }

  // ---- layout, drawing ------------------------------------------------------------------
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
  function relayout(animate = true) {
    const prev = L;
    idx = M.indexMap(map);
    if (focusId && !idx.has(focusId)) focusId = null;
    if (local && (!isBubble() || !idx.has(local.id))) local = null;
    L = layoutMap(map, { focus: focusId, local, positions: isBubble() ? sim.pos : null });
    sel = sel.filter((id) => L.boxes.has(id));
    if (selRel && !map.rels.some((r) => r.id === selRel)) selRel = null;
    cancelAnimationFrame(anim);
    if (isBubble()) syncSim(prev);
    else { stopSim(); if (animate && prev && S().animate !== false && !reduceMotion.matches && L.boxes.size < 400 && !editing) startAnim(prev); else drawContent(L); }
    paintTheme();
    drawUI(); paintBar(); paintCrumbs(); drawMini(); paintOutlineSoon();
    if (editing) placeEditor();
  }
  function drawContent(Lx) {
    const dim = drag?.started && drag.kind === 'node' && !drag.bubble ? drag.branch : null;
    content.innerHTML = contentMarkup(map, Lx, { interactive: true, dim, selRel, editing: editing?.id || null, fade: fadeSet(Lx) });
  }
  /** Bubble maps light up the hovered topic and its neighbours (as Obsidian's graph does). */
  function fadeSet(Lx) {
    if (!isBubble() || !hover || S().hoverHighlight === false || drag?.started || !Lx.boxes.has(hover)) return null;
    const keep = new Set([hover]);
    for (const e of Lx.edges) { if (e.from === hover) keep.add(e.to); if (e.to === hover) keep.add(e.from); }
    for (const r of Lx.rels) { if (r.from === hover) keep.add(r.to); if (r.to === hover) keep.add(r.from); }
    return { id: hover, keep };
  }

  // ---- the bubble map's physics -------------------------------------------------------
  /** After a layout: take over its places, then warm the simulation up (more after a big change). */
  function syncSim(prev) {
    const links = L.links;
    const next = new Map();
    for (const id of links.nodes) {
      const b = L.boxes.get(id); const old = sim.pos.get(id);
      next.set(id, { id, x: b.x + b.w / 2, y: b.y + b.h / 2, vx: old?.vx || 0, vy: old?.vy || 0, r: b.w / 2, fixed: false });
    }
    const changedShape = !prev || !prev.links || prev.links.nodes.length !== links.nodes.length || prev.links.links.length !== links.links.length || links.fresh.length > 0;
    sim.pos = next;
    sim.arr = links.nodes.map((id) => next.get(id));
    drawContent(L);
    if (S().animate === false || reduceMotion.matches) { if (links.fresh.length || !links.anySaved) settleNow(); return; }
    heat(!links.anySaved ? 1 : changedShape ? 0.4 : 0.12);
  }
  function heat(a) {
    sim.alpha = Math.max(sim.alpha, a);
    if (!sim.raf) sim.raf = requestAnimationFrame(tick);
  }
  function stopSim() { cancelAnimationFrame(sim.raf); sim.raf = 0; sim.alpha = 0; }
  function applySim() {
    for (const p of sim.arr) { const b = L.boxes.get(p.id); if (b) { b.x = p.x - b.w / 2; b.y = p.y - b.h / 2; } }
  }
  function tick() {
    sim.raf = 0;
    if (destroyed || !L || !isBubble()) return;
    const f = { ...FORCE_DEFAULTS, ...graphOpts(map) };
    for (const p of sim.arr) { const b = L.boxes.get(p.id); p.r = b ? b.w / 2 : p.r; p.fixed = !!b?.nd.pin || p.id === sim.dragId || (!!sim.dragId && !sim.near.has(p.id)); p.ghost = p.id === sim.dragId; }
    // big maps: several ticks per frame, so they still settle in a few seconds
    const per = sim.arr.length > 250 ? 1 : sim.arr.length > 120 ? 2 : 3;
    for (let i = 0; i < per; i++) { step(sim.arr, L.links.links, sim.alpha, f); sim.alpha = cool(sim.alpha); }
    if (sim.dragId) sim.alpha = Math.max(sim.alpha, 0.25);
    applySim();
    recomputeBounds();
    drawContent(L); drawUI();
    if (editing) placeEditor();
    drawMini();
    if (sim.alpha > ALPHA_MIN || sim.dragId) sim.raf = requestAnimationFrame(tick);
    else { sim.alpha = 0; persistSoon(); }
  }
  /** Run the physics to rest at once (no animation, or reduced motion). */
  function settleNow() {
    const f = { ...FORCE_DEFAULTS, ...graphOpts(map) };
    for (const p of sim.arr) { const b = L.boxes.get(p.id); p.fixed = !!b?.nd.pin; }
    let a = 1;
    for (let t = 0; t < 300 && a > ALPHA_MIN; t++) { step(sim.arr, L.links.links, a, f); a = cool(a); }
    applySim(); recomputeBounds(); drawContent(L); drawUI(); persistSoon();
  }
  function recomputeBounds() {
    let x0 = Infinity; let y0 = Infinity; let x1 = -Infinity; let y1 = -Infinity;
    for (const b of L.boxes.values()) {
      x0 = Math.min(x0, b.x - 16); y0 = Math.min(y0, b.y - 16); x1 = Math.max(x1, b.x + b.w + 16);
      y1 = Math.max(y1, b.y + b.h + (b.lab ? b.lab.h + 8 : 16));
      if (b.lab) { const cx = b.x + b.w / 2; x0 = Math.min(x0, cx - b.lab.w / 2 - 4); x1 = Math.max(x1, cx + b.lab.w / 2 + 4); }
    }
    if (Number.isFinite(x0)) L.bounds = { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  }
  /** Once the bubbles rest, their places are saved in the map (no undo step: nothing was edited). */
  function persistSoon() {
    clearTimeout(sim.persistT);
    sim.persistT = setTimeout(() => {
      if (destroyed || !isBubble() || sim.raf) return;
      let moved = false;
      for (const p of sim.arr) {
        const nd = node(p.id); if (!nd) continue;
        const gx = Math.round(p.x); const gy = Math.round(p.y);
        if (nd.gx !== gx || nd.gy !== gy) { nd.gx = gx; nd.gy = gy; moved = true; }
      }
      if (moved) { ver++; opts.onChange?.(); }
    }, 250);
  }
  /** Forget every place and pin: the bubbles find a fresh arrangement. */
  function rearrange() {
    commit(() => { for (const nd of M.walkAll(map)) { delete nd.gx; delete nd.gy; delete nd.pin; } sim.pos.clear(); }, { animate: false });
    requestAnimationFrame(() => fit());
    setTimeout(() => fit(), 1600);
  }
  function setPin(ids, on) {
    commit(() => { for (const id of ids) { const nd = node(id); const p = sim.pos.get(id); if (!nd) continue; if (on) { nd.pin = true; if (p) { nd.gx = Math.round(p.x); nd.gy = Math.round(p.y); } } else delete nd.pin; } }, { animate: false });
  }
  function startAnim(prev) {
    const from = new Map();
    let moved = false;
    for (const b of L.boxes.values()) {
      let p = prev.boxes.get(b.id);
      if (!p && b.parent) { const pp = prev.boxes.get(b.parent) || L.boxes.get(b.parent); if (pp) p = { x: pp.x + pp.w / 2 - b.w / 2, y: pp.y + pp.h / 2 - b.h / 2 }; }
      if (!p) continue;
      if (Math.abs(p.x - b.x) > 0.5 || Math.abs(p.y - b.y) > 0.5) moved = true;
      from.set(b.id, p);
    }
    if (!moved) { drawContent(L); return; }
    const t0 = performance.now(); const dur = 170; const target = L;
    const frame = (now) => {
      if (L !== target || destroyed) return;
      const t = Math.min(1, (now - t0) / dur);
      const e = 1 - (1 - t) ** 3;
      if (t >= 1) { drawContent(L); return; }
      const boxes = new Map();
      for (const [id, b] of L.boxes) { const f = from.get(id); boxes.set(id, f ? { ...b, x: f.x + (b.x - f.x) * e, y: f.y + (b.y - f.y) * e } : b); }
      drawContent({ ...L, boxes });
      anim = requestAnimationFrame(frame);
    };
    anim = requestAnimationFrame(frame);
  }
  function paintTheme() {
    const T = themeOf(map);
    defs.innerHTML = arrowDefs(T).replace(/^<defs>|<\/defs>$/g, '');
    // the canvas is the map's own background (a map's look is content, not app chrome)
    stage.style.setProperty('--mm-bg', T.bg);
    stage.style.setProperty('--mm-dot', T.bg === '#18181b' ? 'rgb(255 255 255 / 0.09)' : 'rgb(0 0 0 / 0.09)');
    stage.dataset.canvas = S().canvasBg || 'dots';
    stage.classList.toggle('is-darkmap', T.bg === '#18181b');
  }
  function applyView() {
    vp.setAttribute('transform', `translate(${view.x} ${view.y}) scale(${view.k})`);
    const g = 22 * view.k;
    stage.style.setProperty('--mm-grid', `${g}px`);
    stage.style.setProperty('--mm-gx', `${view.x}px`);
    stage.style.setProperty('--mm-gy', `${view.y}px`);
    const pct = zoomBox.querySelector('.mm-zoom-pct');
    if (pct) pct.textContent = `${Math.round(view.k * 100)}%`;
    // dot labels fade out as you zoom far out (Obsidian's text fade), unless Settings say always show
    stage.style.setProperty('--mm-label-o', S().labelFade === false ? '1' : String(Math.max(0, Math.min(1, (view.k - 0.28) / 0.22))));
    if (editing) placeEditor();
    drawMini();
  }
  const rect = () => canvas.getBoundingClientRect();
  const toMap = (cx, cy) => { const r = rect(); return { x: (cx - r.left - view.x) / view.k, y: (cy - r.top - view.y) / view.k }; };

  // ---- selection chrome, hover buttons, drop marks (the UI layer, in map units) ------
  function drawUI() {
    if (!L) return;
    const k = view.k; const T = themeOf(map);
    let s = '';
    const ring = (b, cls, pad = 4) => (b.circle
      ? `<circle class="${cls}" cx="${b.x + b.w / 2}" cy="${b.y + b.h / 2}" r="${b.w / 2 + pad / k}" vector-effect="non-scaling-stroke"/>`
      : `<rect class="${cls}" x="${b.x - pad / k}" y="${b.y - pad / k}" width="${b.w + (pad * 2) / k}" height="${b.h + (pad * 2) / k}" rx="${(b.st.shape === 'pill' ? b.h / 2 : b.st.radius || 4) + pad / k}" vector-effect="non-scaling-stroke"/>`);
    if (findState) for (const id of findState.hits) { const b = L.boxes.get(id); if (b) s += ring(b, id === findState.hits[findState.i] ? 'mm-find-ring is-current' : 'mm-find-ring', 6); }
    for (const id of sel) { const b = L.boxes.get(id); if (b) s += ring(b, 'mm-sel-ring'); }
    // hover and single-selection affordances: expand / collapse and add a child
    const target = drag || editing || locked ? null : hover || (sel.length === 1 ? sel[0] : null);
    const tb = target && L.boxes.get(target);
    if (tb && !(drag && drag.started)) {
      const p = badgePos(tb);
      const unit = 1 / k;
      const hasKids = tb.nd.children.length > 0;
      const along = { right: [1, 0], both: [1, 0], left: [-1, 0], down: [0, 1], up: [0, -1], tree: [0, 1] }[tb.dir] || [1, 0];
      if (hasKids && !tb.hidden) s += `<g class="mm-uibtn" data-toggle="${esc(tb.id)}" transform="translate(${p.x} ${p.y}) scale(${unit})"><circle r="9"/><path d="M-4 0H4"/></g>`;
      const off = (hasKids || tb.hidden ? 38 : 13);
      let ax = tb.dir === 'tree' ? tb.x + tb.w + 14 / k : p.x + along[0] * (off - 13) / k;
      let ay = tb.dir === 'tree' ? tb.y + tb.h / 2 : p.y + along[1] * (off - 13) / k;
      // a bubble: + at its top right, the collapse toggle at its bottom right
      if (tb.dir === 'bubble') { const r = tb.w / 2; if (tb.dot) { ax = p.x + (hasKids || tb.hidden ? 24 : 0) / k; ay = p.y; } else { ax = tb.x + r + r * 0.74 + 8 / k; ay = tb.y + r - r * 0.74 - 8 / k; } }
      s += `<g class="mm-uibtn is-add" data-add="${esc(tb.id)}" transform="translate(${ax} ${ay}) scale(${unit})"><circle r="10"/><path d="M-4.5 0H4.5M0 -4.5V4.5"/></g>`;
    }
    if (drag?.started && drag.kind === 'node' && drag.bubble) {
      const b = drag.target && L.boxes.get(drag.target.id);
      if (b) s += ring(b, 'mm-drop-ring', 6);
    } else if (drag?.started && drag.kind === 'node') {
      const t = drag.target;
      if (t) {
        const b = L.boxes.get(t.id);
        if (b && t.kind === 'child') s += ring(b, 'mm-drop-ring', 5);
        else if (b) {
          const vert = t.axis === 'x';
          const pad = 6 / k;
          if (vert) { const x = t.kind === 'before' ? b.x - pad : b.x + b.w + pad; s += `<path class="mm-drop-line" d="M${x} ${b.y - 2 / k}V${b.y + b.h + 2 / k}" vector-effect="non-scaling-stroke"/>`; }
          else { const y = t.kind === 'before' ? b.y - pad : b.y + b.h + pad; s += `<path class="mm-drop-line" d="M${b.x - 2 / k} ${y}H${b.x + b.w + 2 / k}" vector-effect="non-scaling-stroke"/>`; }
        }
      }
      const g = drag.ghost;
      if (g) s += `<g class="mm-ghost" transform="translate(${g.x} ${g.y})"><rect width="${g.w}" height="${g.h}" rx="8" fill="${T.bg}" vector-effect="non-scaling-stroke"/><text x="${g.w / 2}" y="${g.h / 2}" text-anchor="middle" dominant-baseline="central" font-family="${esc(FONT)}" font-size="${g.fs}" font-weight="600" fill="${T.ink}">${esc(g.text)}</text></g>`;
    }
    if (drag?.kind === 'marquee' && drag.started) {
      const a = drag.a; const b = drag.b;
      s += `<rect class="mm-marquee" x="${Math.min(a.x, b.x)}" y="${Math.min(a.y, b.y)}" width="${Math.abs(a.x - b.x)}" height="${Math.abs(a.y - b.y)}" vector-effect="non-scaling-stroke"/>`;
    }
    if (connectFrom) {
      const b = L.boxes.get(connectFrom);
      if (b) s += `<path class="mm-connect" d="M${b.x + b.w / 2} ${b.y + b.h / 2}L${pointer.x} ${pointer.y}" vector-effect="non-scaling-stroke"/>`;
    }
    ui.innerHTML = s;
  }

  // ---- the minimap ------------------------------------------------------------------
  let miniRaf = 0;
  function drawMini() {
    cancelAnimationFrame(miniRaf);
    miniRaf = requestAnimationFrame(() => {
      if (!L || destroyed) return;
      const r = rect();
      const bw = L.bounds.w * view.k; const bh = L.bounds.h * view.k;
      const show = S().minimap !== false && r.width > 520 && r.height > 360 && (bw > r.width * 0.9 || bh > r.height * 0.9 || view.x + L.bounds.x * view.k < 0 || view.y + L.bounds.y * view.k < 0 || view.x + (L.bounds.x + L.bounds.w) * view.k > r.width || view.y + (L.bounds.y + L.bounds.h) * view.k > r.height);
      mini.hidden = !show;
      if (show) { const b = bar.getBoundingClientRect(); const r2 = mini.getBoundingClientRect(); if (b.width && r2.width && !bar.classList.contains('is-raised') && r2.left < b.right + 8) { mini.hidden = true; return; } }
      if (!show) return;
      const W = mini.width; const H = mini.height;
      const g = mini.getContext('2d');
      const vb = { x: -view.x / view.k, y: -view.y / view.k, w: r.width / view.k, h: r.height / view.k };
      const x0 = Math.min(L.bounds.x, vb.x); const y0 = Math.min(L.bounds.y, vb.y);
      const x1 = Math.max(L.bounds.x + L.bounds.w, vb.x + vb.w); const y1 = Math.max(L.bounds.y + L.bounds.h, vb.y + vb.h);
      const sc = Math.min((W - 12) / (x1 - x0), (H - 12) / (y1 - y0));
      const ox = (W - (x1 - x0) * sc) / 2 - x0 * sc; const oy = (H - (y1 - y0) * sc) / 2 - y0 * sc;
      mini.__t = { sc, ox, oy };
      g.clearRect(0, 0, W, H);
      const T = themeOf(map);
      g.fillStyle = T.bg; g.fillRect(0, 0, W, H);
      for (const b of L.boxes.values()) {
        g.fillStyle = b.st.mode === 'line' ? (b.st.line || T.muted) : b.st.fill !== 'none' && b.st.fill !== T.bg ? b.st.fill : (b.st.stroke !== 'none' ? b.st.stroke : T.muted);
        g.fillRect(ox + b.x * sc, oy + (b.st.mode === 'line' ? b.y + b.h - 2 / sc : b.y) * sc, Math.max(1.5, b.w * sc), Math.max(1.5, b.st.mode === 'line' ? 2 : b.h * sc));
      }
      g.strokeStyle = '#06b6d4'; g.lineWidth = 1.5;
      g.strokeRect(ox + vb.x * sc, oy + vb.y * sc, vb.w * sc, vb.h * sc);
    });
  }
  function miniJump(e) {
    const t = mini.__t; if (!t) return;
    const r = mini.getBoundingClientRect(); const cr = rect();
    const mx = (e.clientX - r.left - t.ox) / t.sc; const my = (e.clientY - r.top - t.oy) / t.sc;
    view.x = cr.width / 2 - mx * view.k; view.y = cr.height / 2 - my * view.k;
    applyView();
  }
  mini.addEventListener('pointerdown', (e) => {
    e.preventDefault(); e.stopPropagation();
    try { mini.setPointerCapture(e.pointerId); } catch (_) { /* synthetic */ }
    miniJump(e);
    const mv = (ev) => miniJump(ev);
    const up = () => { mini.removeEventListener('pointermove', mv); };
    mini.addEventListener('pointermove', mv);
    mini.addEventListener('pointerup', up, { once: true });
    mini.addEventListener('pointercancel', up, { once: true });
  });

  // ---- view: zoom, fit, keep things in sight --------------------------------------------
  const clampK = (k) => Math.min(4, Math.max(0.08, k));
  function zoomAt(cx, cy, f) {
    const r = rect();
    const px = cx - r.left; const py = cy - r.top;
    const k = clampK(view.k * f);
    view.x = px - ((px - view.x) / view.k) * k;
    view.y = py - ((py - view.y) / view.k) * k;
    view.k = k;
    applyView(); drawUI();
  }
  function zoomBy(f) { const r = rect(); zoomAt(r.left + r.width / 2, r.top + r.height / 2, f); }
  function setZoom(k) { zoomBy(k / view.k); }
  /** Fit the whole map (never zooming past 100%). */
  function fit(maxK = 1) {
    if (!L) return;
    const r = rect();
    if (!r.width || !r.height) return;
    const padX = 48; const padTop = 48; const padBottom = 80;
    const k = clampK(Math.min(maxK, (r.width - padX * 2) / L.bounds.w, (r.height - padTop - padBottom) / L.bounds.h));
    view.k = k;
    view.x = (r.width - L.bounds.w * k) / 2 - L.bounds.x * k;
    view.y = padTop + (r.height - padTop - padBottom - L.bounds.h * k) / 2 - L.bounds.y * k;
    applyView(); drawUI();
  }
  function centerOn(id, { zoomIn = false } = {}) {
    const b = L?.boxes.get(id);
    if (!b) return;
    const r = rect();
    if (zoomIn && view.k < 0.6) view.k = 0.8;
    view.x = r.width / 2 - (b.x + b.w / 2) * view.k;
    view.y = r.height / 2 - (b.y + b.h / 2) * view.k;
    applyView(); drawUI();
  }
  function ensureVisible(id) {
    const b = L?.boxes.get(id);
    if (!b) return;
    const r = rect(); const m = 40; const bottom = 76;
    const sx = view.x + b.x * view.k; const sy = view.y + b.y * view.k; const sw = b.w * view.k; const sh = b.h * view.k;
    let dx = 0; let dy = 0;
    if (sx < m) dx = m - sx; else if (sx + sw > r.width - m) dx = r.width - m - (sx + sw);
    if (sy < m) dy = m - sy; else if (sy + sh > r.height - bottom) dy = r.height - bottom - (sy + sh);
    if (dx || dy) { view.x += dx; view.y += dy; applyView(); }
  }

  // ---- selection --------------------------------------------------------------------------
  function select(ids, { keepRel = false } = {}) {
    sel = [...new Set(ids)].filter((id) => L?.boxes.has(id));
    if (!keepRel) selRel = null;
    drawUI(); paintBar(); paintOutlineSel();
    if (sel.length === 1) ensureVisible(sel[0]);
  }
  const primary = () => (sel.length ? sel[sel.length - 1] : null);
  const node = (id) => idx.get(id)?.node || null;

  // ---- editing text in place -----------------------------------------------------------
  function startEdit(id, { replace = null, isNew = false } = {}) {
    if (locked || destroyed) return;
    if (editing) finishEdit();
    let nd = node(id);
    if (!nd) return;
    const before = snap();
    // a topic inside a collapsed branch opens its branch first
    let opened = false;
    for (const a of M.ancestors(idx, id)) if (a.collapsed) { a.collapsed = false; opened = true; }
    if (focusId && id !== focusId && !M.isInside(idx, id, focusId)) { focusId = null; opened = true; }
    sel = [id]; selRel = null;
    editing = { id, isNew, before, orig: nd.text };
    if (replace != null) nd.text = replace;
    if (opened || replace != null) relayout(false); else { drawContent(L); drawUI(); }
    nd = node(id);
    ta.value = nd.text;
    ta.hidden = false;
    placeEditor();
    ta.focus({ preventScroll: true });
    if (replace != null) ta.setSelectionRange(ta.value.length, ta.value.length); else ta.select();
    ensureVisible(id);
    paintBar();
  }
  function placeEditor() {
    if (!editing) return;
    const b = L.boxes.get(editing.id);
    if (!b) { finishEdit(); return; }
    const k = view.k; const st = b.st;
    const contentW = b.left + b.textW + b.right;
    let x0 = (b.w - contentW) / 2 + b.left;
    const lines = b.lines.length;
    let textTop = (b.h - lines * st.lineH) / 2;
    const w = Math.max(b.textW, 24) + 6;
    if (b.dot) { x0 = b.w / 2 - b.textW / 2 + (b.nd.task ? 9 : 0); textTop = b.h + 4; } // a dot's label sits under it
    Object.assign(ta.style, {
      left: `${view.x + (b.x + x0 - 3) * k}px`, top: `${view.y + (b.y + textTop) * k}px`,
      width: `${w * k}px`, height: `${lines * st.lineH * k}px`,
      font: `${st.italic ? 'italic ' : ''}${st.weight} ${st.fontSize * k}px/${st.lineH * k}px ${FONT}`,
      color: b.nd.task === 'done' ? themeOf(map).muted : b.dot ? themeOf(map).ink : st.ink,
      caretColor: b.dot ? themeOf(map).ink : st.ink,
    });
  }
  ta.addEventListener('input', () => {
    if (!editing) return;
    const nd = node(editing.id);
    if (!nd) return;
    nd.text = ta.value;
    relayout(false);
  });
  ta.addEventListener('keydown', (e) => {
    if (e.isComposing || !editing) return;
    e.stopPropagation();
    if (e.key === 'Enter' && !e.shiftKey && !e.altKey) { e.preventDefault(); const id = editing.id; finishEdit(); if (e.metaKey || e.ctrlKey) return; if (S().enterAddsAfterEdit && node(id)) addSibling(); return; }
    if (e.key === 'Tab') { e.preventDefault(); const id = editing.id; finishEdit(); if (node(id) && !e.shiftKey) addChild(); return; }
    if (e.key === 'Escape') { e.preventDefault(); finishEdit(); }
  });
  ta.addEventListener('blur', () => { if (editing && !ta.__keep) finishEdit(); });
  ta.addEventListener('paste', (e) => {
    // several lines pasted into a NEW, empty topic become branches (an outline paste)
    const text = e.clipboardData?.getData('text/plain') || '';
    if (!editing || !editing.isNew || ta.value || !/\n/.test(text.trim())) return;
    e.preventDefault();
    const pid = idx.get(editing.id)?.parent?.id;
    const id = editing.id;
    ta.value = '';
    finishEdit();
    if (pid) pasteBranches(outlineToBranches(text), pid, { replaceId: id });
  });

  /** Save the text being edited (a new topic left empty goes away). */
  function finishEdit() {
    if (!editing) return;
    const ed = editing;
    editing = null;
    ta.hidden = true;
    const nd = node(ed.id);
    if (!nd) { relayout(false); return; }
    let text = ta.value.replace(/\s+$/g, '').replace(/^\s+/, '');
    // typing "[ ] " or "[x] " in front makes it a task
    const tm = text.match(/^\[( |x|X)\]\s+(.*)$/s);
    if (tm) { nd.task = tm[1] === ' ' ? 'todo' : 'done'; text = tm[2]; }
    nd.text = text;
    if (ed.isNew && !text) {
      const parent = idx.get(ed.id)?.parent;
      restore(ed.before);
      changed(false); // the add may have been saved already: save the map without it
      if (parent && L.boxes.has(parent.id)) select([parent.id]); else drawUI();
      canvas.focus({ preventScroll: true });
      return;
    }
    if (text !== ed.orig || ed.isNew || JSON.stringify(JSON.parse(ed.before).map) !== JSON.stringify(map) ) {
      undoStack.push(ed.before);
      if (undoStack.length > 300) undoStack.shift();
      redoStack.length = 0;
      changed(false);
    } else relayout(false);
    if (document.activeElement === ta || document.activeElement === document.body) canvas.focus({ preventScroll: true });
  }

  // ---- adding topics -----------------------------------------------------------------------
  function addChild(parentId = primary(), { edit = true } = {}) {
    if (locked) return;
    const pid = parentId || map.root.id;
    let made = null;
    commit(() => { made = M.addChild(map, idx, pid, ''); if (focusId && pid !== focusId && !M.isInside(idx, pid, focusId)) focusId = null; return !!made; });
    if (!made) return;
    sel = [made.id];
    if (edit) { editing = null; startEditNew(made.id); }
  }
  function addSibling(before = false) {
    if (locked) return;
    const id = primary();
    if (!id) { addChild(map.root.id); return; }
    if (id === focusId) { addChild(id); return; }
    let made = null;
    commit(() => { made = M.addSibling(map, idx, id, before); return !!made; });
    if (!made) return;
    sel = [made.id];
    startEditNew(made.id);
  }
  function addParent() {
    if (locked) return;
    const id = primary();
    if (!id) return;
    let made = null;
    commit(() => { made = M.addParent(map, idx, id); return !!made; });
    if (made) { sel = [made.id]; startEditNew(made.id); }
  }
  /** A floating topic on the canvas at map point p. */
  function addFloating(p) {
    if (locked) return;
    let made = null;
    commit(() => { made = M.node(''); made.x = Math.round(p.x); made.y = Math.round(p.y); map.floating.push(made); if (focusId) focusId = null; return true; });
    sel = [made.id];
    startEditNew(made.id);
  }
  function startEditNew(id) {
    // the snapshot BEFORE the add is what undo (and an empty new topic) returns to
    const before = undoStack.pop();
    startEdit(id, { isNew: true });
    if (editing && before) editing.before = before;
    else if (before) undoStack.push(before);
  }

  // ---- other edits ------------------------------------------------------------------------
  function removeSelected() {
    if (locked) return;
    if (selRel) { const id = selRel; commit(() => { map.rels = map.rels.filter((r) => r.id !== id); selRel = null; }); return; }
    const ids = sel.filter((id) => id !== map.root.id && id !== focusId);
    if (!ids.length) { if (sel.includes(map.root.id)) toast('The central topic stays. Rename it instead.', 2200); return; }
    const first = idx.get(ids[0]);
    const next = (() => {
      const s = M.siblingsOf(map, idx, ids[0]);
      if (s && s.arr !== map.floating) { const alt = s.arr[s.i + 1] || s.arr[s.i - 1]; if (alt && !ids.includes(alt.id)) return alt.id; }
      return first?.parent?.id || map.root.id;
    })();
    let gone = 0;
    commit(() => { gone = M.removeNodes(map, idx, ids).size; return gone > 0; });
    select([next]);
    if (gone > 1) undoToast(`Deleted ${gone} topics.`);
  }
  function undoToast(text) {
    const frag = document.createDocumentFragment();
    frag.append(`${text} `);
    const b = h('button', { class: 'cthw-toast-btn', type: 'button', text: 'Undo' });
    b.addEventListener('click', () => { if (b.disabled) return; b.disabled = true; undo(); });
    frag.append(b);
    toast(frag, 6000);
  }
  function toggleCollapse(id = primary(), deep = false) {
    const nd = node(id);
    if (!nd || !nd.children.length) return;
    const want = !nd.collapsed;
    commit(() => { if (deep) M.setCollapsedDeep(nd, want); else nd.collapsed = want; });
    if (!want && deep) toast('Opened every topic in this branch.', 1600);
  }
  function setAllCollapsed(collapsed) {
    commit(() => { for (const t of M.tops(map)) for (const c of t.children) M.setCollapsedDeep(c, collapsed); if (!collapsed) for (const t of M.tops(map)) t.collapsed = false; });
    requestAnimationFrame(() => fit());
  }
  function expandTo(level) { commit(() => M.expandToLevel(map, level)); requestAnimationFrame(() => fit()); }
  /** Set a property on every selected topic (undefined value removes it). */
  function style(key, value) {
    if (locked || !sel.length) return;
    commit(() => { for (const id of sel) { const nd = node(id); if (!nd) continue; if (value == null || value === '') delete nd[key]; else nd[key] = value; } });
  }
  function toggleFlag(key) {
    if (locked || !sel.length) return;
    const on = !sel.every((id) => node(id)?.[key]);
    style(key, on ? true : null);
  }
  function toggleTask(id = null) {
    const ids = id ? [id] : sel;
    if (locked || !ids.length) return;
    commit(() => {
      const all = ids.map(node).filter(Boolean);
      if (id) { const nd = all[0]; nd.task = nd.task === 'done' ? 'todo' : 'done'; return; }
      const allTasks = all.every((nd) => nd.task);
      for (const nd of all) { if (allTasks) delete nd.task; else if (!nd.task) nd.task = 'todo'; }
    });
  }
  async function editLink(id = primary()) {
    if (locked) return;
    const nd = node(id); if (!nd) return;
    const v = await opts.editLink?.(nd.link || '');
    if (v == null) { canvas.focus({ preventScroll: true }); return; }
    let url = v.trim();
    if (url && !/^[a-z][a-z0-9+.-]*:/i.test(url)) url = `https://${url}`;
    commit(() => { const n2 = node(id); if (!n2) return false; if (url) n2.link = url; else delete n2.link; });
    canvas.focus({ preventScroll: true });
  }
  async function editNote(id = primary()) {
    const nd = node(id); if (!nd) return;
    const v = await opts.editNote?.(nd.note || '', { readOnly: locked, title: nd.text || 'Topic' });
    canvas.focus({ preventScroll: true });
    if (v == null || locked) return;
    const t = v.replace(/\s+$/, '');
    commit(() => { const n2 = node(id); if (!n2) return false; if ((n2.note || '') === t) return false; if (t) n2.note = t; else delete n2.note; });
  }
  function openLink(id) {
    const url = node(id)?.link;
    if (!url) return;
    try { window.open(url, '_blank', 'noopener'); } catch (_) { toast('Your browser blocked the link. Open the map in a full window and try again.'); }
  }
  function focusBranch(id = primary()) {
    if (!id) return;
    if (id === map.root.id && !focusId) { toast('This is already the whole map.', 1600); return; }
    focusId = id === map.root.id ? null : id;
    const nd = node(id); if (nd) nd.collapsed = false;
    relayout(false);
    requestAnimationFrame(() => { fit(); select([id]); });
  }
  function exitFocus() { if (!focusId) return; const id = focusId; focusId = null; relayout(false); requestAnimationFrame(() => { fit(); select([id]); }); }
  function startConnect(id = primary()) {
    if (locked || !id) return;
    connectFrom = id;
    showHint('Click the topic to connect to. Escape cancels.');
    drawUI();
  }
  function endConnect(toId) {
    const from = connectFrom;
    connectFrom = null; hideHint();
    if (!toId || !from || toId === from) { drawUI(); return; }
    if (map.rels.some((r) => (r.from === from && r.to === toId) || (r.from === toId && r.to === from))) { toast('Those two topics are already connected.', 1800); drawUI(); return; }
    const rel = { id: M.uid(), from, to: toId };
    commit(() => { map.rels.push(rel); });
    selRel = rel.id; sel = [];
    drawUI(); paintBar(); drawContent(L);
  }
  async function editRelLabel(id = selRel) {
    const r = map.rels.find((x) => x.id === id);
    if (!r || locked) return;
    const v = await opts.editLabel?.(r.label || '');
    canvas.focus({ preventScroll: true });
    if (v == null) return;
    commit(() => { const x = map.rels.find((y) => y.id === id); if (!x) return false; if (v.trim()) x.label = v.trim(); else delete x.label; });
  }
  function relSet(fn) { const id = selRel; commit(() => { const r = map.rels.find((x) => x.id === id); if (!r) return false; fn(r); }); selRel = id; paintBar(); drawContent(L); }
  function moveSel(delta) {
    const id = primary(); if (!id || locked) return;
    commit(() => M.reorder(map, idx, id, delta));
  }
  function promote() { const id = primary(); if (id && !locked) commit(() => M.promote(map, idx, id)); }
  function demote() { const id = primary(); if (id && !locked) commit(() => M.demote(map, idx, id)); }
  function detach(id = primary()) {
    if (locked || !id || M.isTop(map, id)) return;
    const b = L.boxes.get(id);
    commit(() => M.moveNode(map, idx, id, null, -1, { x: (b?.x || 0) + 40, y: (b?.y || 0) + 40 }));
  }
  function duplicate() {
    if (locked || !sel.length) return;
    const made = [];
    commit(() => {
      for (const id of topLevelSel()) {
        const e = idx.get(id);
        if (!e || id === map.root.id) continue;
        const c = M.cloneBranch(e.node, true);
        if (!e.parent) { c.x = (e.node.x || 0) + 40; c.y = (e.node.y || 0) + 40; map.floating.push(c); } else { const arr = e.parent.children; arr.splice(arr.indexOf(e.node) + 1, 0, c); }
        made.push(c.id);
      }
      return made.length > 0;
    });
    if (made.length) select(made);
  }
  /** The selected topics without the ones already inside another selected topic. */
  const topLevelSel = () => sel.filter((id) => !M.ancestors(idx, id).some((a) => sel.includes(a.id)));

  // ---- clipboard (native copy / cut / paste events: no permission prompts) --------------
  const CLIP_KEY = 'cthmm.clip.v1';
  let clip = null;
  function copySel(e) {
    const ids = topLevelSel();
    if (!ids.length) return false;
    const branches = ids.map((id) => node(id)).filter(Boolean).map((nd) => JSON.parse(JSON.stringify(nd)));
    const text = branches.map((b) => mapToText(map, b)).join('\n');
    clip = { text, branches };
    try { localStorage.setItem(CLIP_KEY, JSON.stringify(clip)); } catch (_) { /* memory only */ }
    if (e?.clipboardData) { e.clipboardData.setData('text/plain', text); e.preventDefault(); }
    else navigator.clipboard?.writeText(text).catch(() => {});
    return true;
  }
  function pasteBranches(branches, parentId, { replaceId = null } = {}) {
    if (!branches.length) return;
    const made = [];
    commit(() => {
      const p = node(parentId) || map.root;
      let at = -1;
      if (replaceId) { const i = p.children.findIndex((c) => c.id === replaceId); if (i >= 0) { p.children.splice(i, 1); at = i; } }
      for (const b of branches) { const c = M.cloneBranch(b); if (at >= 0) p.children.splice(at++, 0, c); else p.children.push(c); made.push(c.id); }
      p.collapsed = false;
    });
    select(made);
    if (made.length > 1 || branches.some((b) => b.children?.length)) toast(`Pasted ${branches.reduce((a, b) => a + M.count(b), 0)} topics.`, 1600);
  }
  const onCopy = (e) => { if (!active() || editing) return; if (copySel(e)) toast('Copied.', 1000); };
  const onCut = (e) => { if (!active() || editing || locked) return; if (copySel(e)) removeSelected(); };
  const onPaste = (e) => {
    if (!active() || editing || locked) return;
    const text = e.clipboardData?.getData('text/plain') || '';
    if (!clip) { try { clip = JSON.parse(localStorage.getItem(CLIP_KEY) || 'null'); } catch (_) { clip = null; } }
    let branches;
    if (clip && clip.branches && (!text || text === clip.text)) branches = clip.branches; // ours: keeps colours, tasks, notes
    else if (text.trim()) branches = outlineToBranches(text);
    else return;
    e.preventDefault();
    pasteBranches(branches, primary() || map.root.id);
  };
  window.addEventListener('copy', onCopy);
  window.addEventListener('cut', onCut);
  window.addEventListener('paste', onPaste);
  const active = () => !destroyed && wrap.isConnected && (wrap.contains(document.activeElement) || document.activeElement === document.body) && !document.querySelector('.cthw-overlay');

  // ---- find ----------------------------------------------------------------------------------
  function openFind() {
    if (!findState) findState = { q: '', hits: [], i: 0 };
    findBar.hidden = false;
    findBar.replaceChildren();
    const input = h('input', { class: 'cthd-input mm-find-input', type: 'search', placeholder: 'Find topics and notes', value: findState.q, spellcheck: 'false', 'aria-label': 'Find' });
    const countEl = h('span', { class: 'mm-find-count' });
    const run = () => {
      findState.q = input.value;
      findState.hits = M.findMatches(map, input.value);
      findState.i = Math.min(findState.i, Math.max(0, findState.hits.length - 1));
      countEl.textContent = input.value ? (findState.hits.length ? `${findState.i + 1} of ${findState.hits.length}` : 'No matches') : '';
      showHit();
    };
    const step = (d) => { if (!findState.hits.length) return; findState.i = (findState.i + d + findState.hits.length) % findState.hits.length; run(); };
    input.addEventListener('input', () => { findState.i = 0; run(); });
    input.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') { e.preventDefault(); step(e.shiftKey ? -1 : 1); }
      if (e.key === 'Escape') { e.preventDefault(); closeFind(); }
    });
    findBar.append(icon('search'), input, countEl,
      tbtn('chevron-up', 'Previous match (Shift+Enter)', () => step(-1)),
      tbtn('chevron-down', 'Next match (Enter)', () => step(1)),
      tbtn('x', 'Close (Escape)', () => closeFind()));
    run();
    setTimeout(() => { input.focus(); input.select(); }, 0);
  }
  function showHit() {
    const id = findState?.hits[findState.i];
    if (id && !L.boxes.has(id)) {
      // inside a collapsed branch (or outside the focused one): open the way to it
      for (const a of M.ancestors(idx, id)) a.collapsed = false;
      if (focusId && !M.isInside(idx, id, focusId) && id !== focusId) focusId = null;
      changed(false);
    }
    drawUI();
    if (id) { sel = [id]; drawUI(); paintBar(); centerOn(id); }
  }
  function closeFind() { findState = null; findBar.hidden = true; drawUI(); canvas.focus({ preventScroll: true }); }

  // ---- hints ---------------------------------------------------------------------------------
  let hintT = 0;
  function showHint(text, ms = 0) { hint.textContent = text; hint.hidden = false; clearTimeout(hintT); if (ms) hintT = setTimeout(hideHint, ms); }
  function hideHint() { hint.hidden = true; }

  // ---- the context toolbar (bottom centre) and the zoom controls ----------------------------
  const tip = h('div', { class: 'cthw-tip mm-tip', role: 'tooltip' });
  function showTip(b) {
    const text = b.dataset.tip; if (!text) return;
    if (!tip.isConnected) document.body.append(tip);
    tip.textContent = text; tip.classList.add('is-on');
    const r = b.getBoundingClientRect(); const t = tip.getBoundingClientRect();
    tip.style.left = `${Math.max(8, Math.min(innerWidth - t.width - 8, r.left + r.width / 2 - t.width / 2))}px`;
    tip.style.top = `${Math.max(8, r.top - t.height - 8)}px`;
  }
  const hideTip = () => tip.classList.remove('is-on');
  function tbtn(iconName, tipText, onClick, { on = false, disabled = false, html = null, cls = '' } = {}) {
    const b = h('button', { class: `mm-tb-btn${on ? ' is-on' : ''} ${cls}`.trim(), type: 'button', 'aria-label': tipText, 'data-tip': tipText, disabled: disabled || null, 'aria-pressed': on ? 'true' : null });
    if (html) b.innerHTML = html; else b.append(icon(iconName));
    b.addEventListener('pointerenter', () => showTip(b));
    b.addEventListener('pointerleave', hideTip);
    b.addEventListener('focus', () => showTip(b));
    b.addEventListener('blur', hideTip);
    b.addEventListener('click', (e) => { hideTip(); onClick(e, b); });
    return b;
  }
  const sep = () => h('span', { class: 'mm-tb-sep', 'aria-hidden': 'true' });

  let pop = null;
  function closePop() { if (pop) { pop.remove(); pop = null; } }
  /** A small panel above a toolbar button. build(el, close) fills it. */
  function popover(anchor, build) {
    closePop();
    const el = h('div', { class: 'mm-pop cthd-layer', role: 'dialog' });
    pop = el;
    build(el, () => { closePop(); canvas.focus({ preventScroll: true }); });
    document.body.append(el);
    const r = anchor.getBoundingClientRect(); const p = el.getBoundingClientRect();
    el.style.left = `${Math.max(8, Math.min(innerWidth - p.width - 8, r.left + r.width / 2 - p.width / 2))}px`;
    const above = r.top - p.height - 10;
    el.style.top = `${above >= 8 ? above : Math.min(innerHeight - p.height - 8, r.bottom + 10)}px`;
    const away = (ev) => { if (!el.contains(ev.target) && ev.target !== anchor && !anchor.contains(ev.target)) { closePop(); window.removeEventListener('pointerdown', away, true); } };
    setTimeout(() => window.addEventListener('pointerdown', away, true), 0);
    el.addEventListener('keydown', (ev) => { if (ev.key === 'Escape') { ev.stopPropagation(); closePop(); canvas.focus({ preventScroll: true }); } });
    el.querySelector('button')?.focus();
  }
  /** A grid of choices in a popover. */
  function choices(title, items, current, pick, { cols = 3 } = {}) {
    return (el, close) => {
      el.append(h('div', { class: 'mm-pop-title', text: title }));
      const grid = h('div', { class: 'mm-pop-grid', style: { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` } });
      for (const it of items) {
        const b = h('button', { class: `mm-choice${it.value === current ? ' is-on' : ''}`, type: 'button', 'aria-pressed': it.value === current ? 'true' : 'false', title: it.label });
        if (it.html) b.insertAdjacentHTML('beforeend', it.html);
        if (it.swatch) b.append(h('span', { class: 'mm-swatch', style: { background: it.swatch } }));
        b.append(h('span', { class: 'mm-choice-label', text: it.label }));
        b.addEventListener('click', () => { close(); pick(it.value); });
        grid.append(b);
      }
      el.append(grid);
    };
  }
  const selNodes = () => sel.map(node).filter(Boolean);
  const same = (key) => { const v = selNodes().map((nd) => nd[key] ?? null); return v.every((x) => x === v[0]) ? v[0] : '__mixed'; };

  function colorPop(anchor) {
    const cur = same('color');
    popover(anchor, (el, close) => {
      el.append(h('div', { class: 'mm-pop-title', text: 'Colour (the branch under it follows)' }));
      const grid = h('div', { class: 'mm-colors' });
      const add = (val, hex, label) => {
        const b = h('button', { class: `mm-color${(cur ?? '') === (val ?? '') ? ' is-on' : ''}`, type: 'button', 'aria-label': label, title: label, style: { '--c': hex || 'transparent' } });
        if (!val) b.append(icon('x'));
        b.addEventListener('click', () => { close(); style('color', val); });
        grid.append(b);
      };
      add(null, null, 'Automatic');
      for (const [k, hex] of COLORS) add(k, hex, k[0].toUpperCase() + k.slice(1));
      el.append(grid);
    });
  }
  function shapePop(anchor) {
    const cur = same('shape'); const curFill = same('fill');
    popover(anchor, (el, close) => {
      choices('Shape', [{ value: null, label: 'Theme', html: SHAPE_GLYPH.rounded.replace('rx="4"', 'rx="4" stroke-dasharray="3 2"') }, ...M.SHAPES.map((s) => ({ value: s, label: SHAPE_LABEL[s], html: SHAPE_GLYPH[s] }))], cur, (v) => style('shape', v), { cols: 4 })(el, close);
      choices('Fill', [{ value: null, label: 'Theme' }, ...M.FILLS.map((f) => ({ value: f, label: FILL_LABEL[f] }))], curFill, (v) => style('fill', v), { cols: 4 })(el, close);
    });
  }
  function sizePop(anchor) {
    popover(anchor, choices('Text size', [{ value: null, label: 'Default' }, ...M.SIZES.map((s) => ({ value: s, label: SIZE_LABEL[s] }))], same('size'), (v) => style('size', v === 'm' ? null : v), { cols: 5 }));
  }
  const mapSet = (key, value) => {
    if (locked) return;
    commit(() => { if (map[key] === value) return false; map[key] = value; });
    if (key === 'layout') { requestAnimationFrame(() => fit()); if (value === 'bubble') setTimeout(() => fit(), 1500); }
  };

  // ---- the bubble map's panel: style and forces (Obsidian's graph settings) -------------
  const GRAPH_GLYPH = {
    bubbles: glyph('<circle cx="16" cy="11" r="6"/><circle cx="5.5" cy="6" r="3.5"/><circle cx="26.5" cy="16" r="3.5"/><path d="M8.6 7.5L10.6 8.6M21.5 13.4L23.4 14.5"/>'),
    dots: glyph('<circle cx="16" cy="9" r="2.6" fill="currentColor"/><circle cx="6" cy="5" r="1.6" fill="currentColor"/><circle cx="26" cy="14" r="1.6" fill="currentColor"/><path d="M8 6L14 8.2M24.5 13.2L18 10"/><path d="M12 15.5H20M3 9H9M22 18H30" opacity="0.5"/>'),
  };
  const FORCES = [['repel', 'Spread', 'How hard topics push apart'], ['distance', 'Link length', 'How long the lines between topics are'], ['link', 'Link pull', 'How tightly linked topics hold together'], ['center', 'Pull to the centre', 'How strongly everything gathers in the middle']];
  function graphSet(patch, { commitIt = true } = {}) {
    const apply = () => { map.graph = { ...(map.graph || {}), ...patch }; };
    if (commitIt) commit(() => { apply(); }, { animate: false });
    else { apply(); relayout(false); }
    heat(0.6);
  }
  function graphPop(anchor) {
    const G = graphOpts(map);
    popover(anchor, (el, close) => {
      choices('Bubble style', [{ value: 'bubbles', label: 'Bubbles (text inside)', html: GRAPH_GLYPH.bubbles }, { value: 'dots', label: 'Dots (like Obsidian)', html: GRAPH_GLYPH.dots }], G.style, (v) => graphSet({ style: v }), { cols: 2 })(el, close);
      el.append(h('div', { class: 'mm-pop-title', text: 'Forces' }));
      for (const [key, label, tipText] of FORCES) {
        const val = h('span', { class: 'mm-force-val', text: `${Number(G[key]).toFixed(1)}x` });
        const input = h('input', { class: 'mm-range', type: 'range', min: '0.2', max: '3', step: '0.1', value: String(G[key]), 'aria-label': label, title: tipText });
        let before = null;
        input.addEventListener('input', () => { if (!before) before = snap(); val.textContent = `${Number(input.value).toFixed(1)}x`; graphSet({ [key]: Number(input.value) }, { commitIt: false }); });
        input.addEventListener('change', () => { if (!before) return; undoStack.push(before); redoStack.length = 0; before = null; ver++; opts.onChange?.(); });
        el.append(h('label', { class: 'mm-force', title: tipText }, h('span', { class: 'mm-force-label', text: label }), input, val));
      }
      const row = h('div', { class: 'mm-pop-row' });
      const b = (text, run, tipText) => { const x = h('button', { class: 'mm-choice is-text', type: 'button', text, title: tipText }); x.addEventListener('click', () => { close(); run(); }); return x; };
      row.append(
        b('Re-arrange', () => rearrange(), 'Forget every place and pin: the bubbles find a fresh arrangement'),
        b('Unpin all', () => setPin([...M.walkAll(map)].filter((nd) => nd.pin).map((nd) => nd.id), false), 'Let every pinned topic move again'),
        b('Reset forces', () => graphSet({ ...FORCE_DEFAULTS }), 'Every force back to 1x'));
      el.append(row);
    });
  }
  function localPop(anchor, id = primary()) {
    if (!id) return;
    popover(anchor, choices('Local graph: only what is near this topic', [1, 2, 3].map((d) => ({ value: d, label: `${d} link${d === 1 ? '' : 's'} away` })), local?.id === id ? local.depth : null, (v) => showLocal(id, v), { cols: 3 }));
  }
  function showLocal(id, depth) {
    local = { id, depth };
    relayout(false);
    heat(0.3);
    requestAnimationFrame(() => { fit(); select([id]); });
    setTimeout(() => { if (local) fit(); }, 900);
  }
  function exitLocal() {
    if (!local) return;
    const id = local.id; local = null;
    relayout(false);
    requestAnimationFrame(() => { fit(); select([id]); });
  }
  function layoutPop(anchor) {
    popover(anchor, choices('Layout', M.LAYOUTS.map((l) => ({ value: l, label: LAYOUT_LABEL[l], html: LAYOUT_GLYPH[l] })), map.layout, (v) => mapSet('layout', v)));
  }
  function linePop(anchor) {
    popover(anchor, (el, close) => {
      choices('Branch lines', M.LINES.map((l) => ({ value: l, label: LINE_LABEL[l], html: LINE_GLYPH[l] })), map.line, (v) => mapSet('line', v), { cols: 4 })(el, close);
      choices('Spacing', M.SPACINGS.map((s) => ({ value: s, label: SPACING_LABEL[s] })), map.spacing, (v) => mapSet('spacing', v), { cols: 3 })(el, close);
    });
  }
  function themePop(anchor) {
    popover(anchor, choices('Theme', Object.entries(THEMES).map(([k, t]) => ({ value: k, label: t.label, html: `<span class="mm-theme-chip" style="background:${t.bg};border-color:${t.bg === '#18181b' ? '#3f3f46' : '#e4e4e7'}"><i style="background:${t.root}"></i><i style="background:${t.mono ? t.ink : '#2563eb'}"></i><i style="background:${t.mono ? '#a1a1aa' : '#db2777'}"></i></span>` })), map.theme, (v) => mapSet('theme', v), { cols: 5 }));
  }
  function levelsPop(anchor) {
    popover(anchor, (el, close) => {
      el.append(h('div', { class: 'mm-pop-title', text: 'Show levels' }));
      const row = h('div', { class: 'mm-pop-grid', style: { gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' } });
      for (const [v, label] of [[1, '1'], [2, '2'], [3, '3'], [4, '4'], [99, 'All']]) {
        const b = h('button', { class: 'mm-choice is-text', type: 'button', text: label, title: v === 99 ? 'Open every topic' : `Show ${v} level${v === 1 ? '' : 's'}` });
        b.addEventListener('click', () => { close(); if (v === 99) setAllCollapsed(false); else expandTo(v); });
        row.append(b);
      }
      el.append(row);
    });
  }

  function paintBar() {
    if (!L) return;
    hideTip();
    const items = [];
    if (locked) {
      items.push(h('span', { class: 'mm-bar-note', text: 'View only' }), tbtn('lock-open', 'Unlock editing', () => setLocked(false)));
      if (sel.length === 1 && node(sel[0])?.children.length) items.push(sep(), tbtn(node(sel[0]).collapsed ? 'chevrons-up-down' : 'chevrons-down-up', node(sel[0]).collapsed ? 'Expand (Space)' : 'Collapse (Space)', () => toggleCollapse()));
      if (sel.length === 1 && node(sel[0])?.note) items.push(tbtn('sticky-note', 'Read the note', () => editNote()));
    } else if (selRel) {
      const r = map.rels.find((x) => x.id === selRel);
      items.push(
        tbtn('type', r?.label ? 'Edit the label' : 'Add a label', () => editRelLabel()),
        tbtn('arrow-left-right', 'Reverse the arrow', () => relSet((x) => { const f = x.from; x.from = x.to; x.to = f; })),
        tbtn('spline', r?.bend === 0 ? 'Curve the line' : 'Straighten the line', () => relSet((x) => { x.bend = x.bend === 0 ? 0.25 : 0; })),
        tbtn('spline', 'Bend the other way', () => relSet((x) => { x.bend = -(x.bend == null ? 0.25 : x.bend || 0.25); }), { cls: 'is-flipped' }),
        sep(),
        tbtn('trash-2', 'Delete the relationship (Delete)', () => removeSelected(), { cls: 'is-danger' }));
    } else if (sel.length) {
      const nds = selNodes();
      const one = nds.length === 1 ? nds[0] : null;
      const col = colorHex(same('color') === '__mixed' ? null : same('color')) || (one ? L.boxes.get(one.id)?.color : null) || themeOf(map).ink;
      items.push(
        tbtn('corner-down-right', 'Add child topic (Tab)', () => addChild()),
        tbtn('list-plus', 'Add sibling topic (Enter)', () => addSibling(), { disabled: !!one && (one.id === map.root.id || one.id === focusId) }),
        sep(),
        tbtn(null, 'Colour', (e, b) => colorPop(b), { html: `<span class="mm-tb-color" style="--c:${col}"></span>` }),
        tbtn('shapes', 'Shape and fill', (e, b) => shapePop(b)),
        tbtn('bold', `Bold (${MOD}+B)`, () => toggleFlag('bold'), { on: nds.every((nd) => nd.bold) }),
        tbtn('italic', `Italic (${MOD}+I)`, () => toggleFlag('italic'), { on: nds.every((nd) => nd.italic) }),
        tbtn('a-large-small', 'Text size', (e, b) => sizePop(b)),
        sep(),
        tbtn('square-check-big', 'Task checkbox (click the box to tick it)', () => toggleTask(), { on: nds.every((nd) => nd.task) }),
        tbtn('link', one?.link ? `Edit the link (${MOD}+K)` : `Add a link (${MOD}+K)`, () => editLink(), { on: !!one?.link, disabled: !one }),
        tbtn('sticky-note', one?.note ? 'Edit the note' : 'Add a note', () => editNote(), { on: !!one?.note, disabled: !one }),
        sep(),
        tbtn('waypoints', 'Connect to another topic (relationship line)', () => startConnect(), { disabled: !one }),
        one && one.children.length ? tbtn(one.collapsed ? 'chevrons-up-down' : 'chevrons-down-up', one.collapsed ? 'Expand (Space)' : 'Collapse (Space)', () => toggleCollapse()) : null,
        isBubble() && one ? tbtn(one.pin ? 'pin-off' : 'pin', one.pin ? 'Unpin (let it move again)' : 'Pin it where it is', () => setPin([one.id], !one.pin), { on: !!one.pin }) : null,
        isBubble() && one ? tbtn('share-2', 'Local graph: only what is near this topic', (e, b) => localPop(b), { on: local?.id === one.id }) : null,
        tbtn('focus', one && one.id === focusId ? 'Show the whole map' : 'Focus on this branch', () => (one && one.id === focusId ? exitFocus() : focusBranch()), { disabled: !one, on: !!one && one.id === focusId }),
        sep(),
        tbtn('trash-2', 'Delete (Delete)', () => removeSelected(), { cls: 'is-danger', disabled: nds.every((nd) => nd.id === map.root.id || nd.id === focusId) }));
    } else {
      items.push(
        tbtn(null, `Layout: ${LAYOUT_LABEL[map.layout]}`, (e, b) => layoutPop(b), { html: LAYOUT_GLYPH[map.layout], cls: 'is-glyph' }),
        tbtn(null, `Branch lines: ${LINE_LABEL[map.line]}`, (e, b) => linePop(b), { html: LINE_GLYPH[map.line], cls: 'is-glyph' }),
        tbtn('palette', `Theme: ${themeOf(map).label}`, (e, b) => themePop(b)),
        isBubble() ? tbtn('orbit', 'Bubble map: style, forces, re-arrange', (e, b) => graphPop(b)) : null,
        sep(),
        tbtn('chevrons-up-down', 'Expand everything', () => setAllCollapsed(false)),
        tbtn('chevrons-down-up', 'Collapse to the main topics', () => setAllCollapsed(true)),
        tbtn('rows-3', 'Show levels', (e, b) => levelsPop(b)),
        sep(),
        tbtn('circle-plus', 'Add a floating topic (or double-click the canvas)', () => { const r = rect(); addFloating(toMap(r.left + r.width / 2 + 60, r.top + r.height / 2 - 80)); }));
    }
    bar.replaceChildren(...items.filter(Boolean));
    bar.hidden = !!editing;
    requestAnimationFrame(placeBar);
  }
  /** Keep the toolbar clear of the zoom controls (it rises) and the minimap (it steps aside). */
  function placeBar() {
    if (destroyed || bar.hidden) return;
    bar.classList.remove('is-raised');
    const b = bar.getBoundingClientRect();
    if (!b.width) return;
    const across = (el) => { const r = el.getBoundingClientRect(); return r.width && r.right > b.left - 8 && r.left < b.right + 8; };
    if (across(zoomBox)) bar.classList.add('is-raised');
    if (!mini.hidden && across(mini)) mini.hidden = true;
  }
  function paintZoom() {
    zoomBox.replaceChildren(
      tbtn('minus', `Zoom out (${MOD}+-)`, () => zoomBy(1 / 1.25)),
      h('button', { class: 'mm-zoom-pct', type: 'button', text: `${Math.round(view.k * 100)}%`, 'aria-label': 'Zoom options', onclick: (e) => menu(e, [
        { label: 'Zoom to fit', icon: 'maximize', run: () => fit() },
        { label: 'Centre the central topic', icon: 'locate-fixed', run: () => centerOn(focusId || map.root.id) },
        'sep',
        { label: '50%', run: () => setZoom(0.5) }, { label: '100%', run: () => setZoom(1) }, { label: '150%', run: () => setZoom(1.5) }, { label: '200%', run: () => setZoom(2) },
      ]) }),
      tbtn('plus', `Zoom in (${MOD}+=)`, () => zoomBy(1.25)),
      tbtn('maximize', 'Zoom to fit (Shift+1)', () => fit()));
  }
  function paintCrumbs() {
    crumbs.hidden = !focusId && !local;
    if (local) {
      crumbs.replaceChildren(icon('share-2'), h('span', { class: 'mm-crumb is-current', text: `Local graph: ${node(local.id)?.text || 'Topic'}` }));
      for (const d of [1, 2, 3]) {
        const b = h('button', { class: `mm-crumb${local.depth === d ? ' is-current' : ''}`, type: 'button', text: `${d}`, title: `${d} link${d === 1 ? '' : 's'} away` });
        b.addEventListener('click', () => showLocal(local.id, d));
        crumbs.append(b);
      }
      crumbs.append(tbtn('x', 'Show the whole map (Escape)', () => exitLocal()));
      return;
    }
    if (!focusId) return;
    const chain = [...M.ancestors(idx, focusId)].reverse();
    crumbs.replaceChildren(icon('focus'));
    for (const a of chain) {
      const b = h('button', { class: 'mm-crumb', type: 'button', text: a.text || 'Topic' });
      b.addEventListener('click', () => (a.id === map.root.id ? exitFocus() : focusBranch(a.id)));
      crumbs.append(b, h('span', { class: 'mm-crumb-sep', text: '/' }));
    }
    crumbs.append(h('span', { class: 'mm-crumb is-current', text: node(focusId)?.text || 'Topic' }), tbtn('x', 'Show the whole map (Escape)', () => exitFocus()));
  }

  // ---- the outline panel -----------------------------------------------------------------
  let olT = 0;
  function paintOutlineSoon() { if (!outlineOpen) return; clearTimeout(olT); olT = setTimeout(paintOutline, 60); }
  function paintOutline() {
    if (!outlineOpen || destroyed) return;
    const list = h('div', { class: 'mm-ol-list', role: 'tree' });
    const row = (nd, depth) => {
      const r = h('div', { class: `mm-ol-row${sel.includes(nd.id) ? ' is-sel' : ''}`, role: 'treeitem', 'data-id': nd.id, style: { paddingLeft: `${8 + depth * 16}px` } });
      if (nd.children.length) {
        const ch = h('button', { class: 'mm-ol-chev', type: 'button', 'aria-label': nd.collapsed ? 'Expand' : 'Collapse' }, icon(nd.collapsed ? 'chevron-right' : 'chevron-down'));
        ch.addEventListener('click', (e) => { e.stopPropagation(); toggleCollapse(nd.id); });
        r.append(ch);
      } else r.append(h('span', { class: 'mm-ol-chev' }));
      if (nd.task) r.append(h('span', { class: `mm-ol-task${nd.task === 'done' ? ' is-done' : ''}` }));
      r.append(h('span', { class: `mm-ol-text${nd.task === 'done' ? ' is-done' : ''}${nd.text ? '' : ' is-empty'}`, text: nd.text || 'Topic' }));
      if (nd.note) r.append(icon('sticky-note', 'mm-ol-ic'));
      if (nd.link) r.append(icon('link', 'mm-ol-ic'));
      r.addEventListener('click', () => { if (!L.boxes.has(nd.id)) { commit(() => { for (const a of M.ancestors(idx, nd.id)) a.collapsed = false; if (focusId) focusId = null; }, { animate: false }); } select([nd.id]); centerOn(nd.id); });
      r.addEventListener('dblclick', () => startEdit(nd.id));
      list.append(r);
      if (!nd.collapsed) for (const c of nd.children) row(c, depth + 1);
    };
    M.tops(map).forEach((t, i) => { if (i === 1) list.append(h('div', { class: 'mm-ol-sec', text: 'Floating topics' })); row(t, 0); });
    const scroll = outline.querySelector('.mm-ol-list')?.scrollTop || 0;
    outline.replaceChildren(h('div', { class: 'mm-ol-head' }, h('span', { text: 'Outline' }), h('span', { class: 'mm-ol-count', text: `${[...M.walkAll(map)].length} topics` }), tbtn('x', 'Close the outline', () => setOutline(false))), list);
    list.scrollTop = scroll;
  }
  function paintOutlineSel() {
    if (!outlineOpen) return;
    outline.querySelectorAll('.mm-ol-row').forEach((r) => r.classList.toggle('is-sel', sel.includes(r.dataset.id)));
    outline.querySelector('.mm-ol-row.is-sel')?.scrollIntoView?.({ block: 'nearest' });
  }
  function setOutline(on) {
    outlineOpen = on;
    outline.hidden = !on;
    opts.setSettings?.({ outline: on });
    if (on) paintOutline();
    requestAnimationFrame(() => { applyView(); opts.onOutline?.(on); });
  }

  // ---- lock ----------------------------------------------------------------------------------
  function setLocked(on) {
    if (editing) finishEdit();
    locked = !!on;
    connectFrom = null;
    stage.classList.toggle('is-locked', locked);
    paintBar(); drawUI();
    opts.onLock?.(locked);
  }

  // ---- pointer: select, drag topics, pan, marquee, pinch ------------------------------------
  const pts = new Map(); // touch pointers for pinch
  let pinch = null;
  function targetOf(e) {
    const t = e.target;
    const g = (a) => t.closest?.(`[${a}]`)?.getAttribute(a);
    return { toggle: g('data-toggle'), add: g('data-add'), task: g('data-task'), link: g('data-link'), note: g('data-note'), rel: g('data-rel'), id: g('data-id') };
  }
  function boxAt(p, pad = 0, skip = null) {
    let hit = null;
    for (const b of L.boxes.values()) {
      if (skip && skip.has(b.id)) continue;
      if (p.x >= b.x - pad && p.x <= b.x + b.w + pad && p.y >= b.y - pad && p.y <= b.y + b.h + pad) hit = b;
    }
    return hit;
  }
  function dropTarget(p) {
    const b = boxAt(p, 6, drag.branch);
    if (!b) return null;
    const e = idx.get(b.id);
    if (!e.parent || b.id === focusId) return { kind: 'child', id: b.id };
    const axis = b.dir === 'down' || b.dir === 'up' ? 'x' : 'y';
    const t = axis === 'x' ? (p.x - b.x) / b.w : (p.y - b.y) / b.h;
    if (t < 0.27) return { kind: 'before', id: b.id, axis };
    if (t > 0.73) return { kind: 'after', id: b.id, axis };
    return { kind: 'child', id: b.id };
  }
  const sideAt = (p) => { const rb = L.boxes.get(map.root.id); return rb && p.x < rb.x + rb.w / 2 ? 'left' : 'right'; };

  function onDown(e) {
    if (destroyed) return;
    engaged = true;
    closePop();
    drag = null; // a gesture whose release never arrived (a menu opened under the pointer) is over
    if (e.button === 2) return; // right-click: the context menu handles it
    if (e.pointerType === 'touch') {
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pts.size === 2) { const [a, b] = [...pts.values()]; pinch = { d: Math.hypot(a.x - b.x, a.y - b.y), cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 }; drag = null; return; }
    }
    if (editing && e.target !== ta) finishEdit();
    if (e.target.closest?.('.mm-bar, .mm-zoom, .mm-find, .mm-crumbs, .mm-mini, .mm-outline')) return;
    canvas.focus({ preventScroll: true });
    const t = targetOf(e);
    const p = toMap(e.clientX, e.clientY);
    const panNow = e.button === 1 || spaceDown;
    if (!panNow && e.button === 0) {
      if (t.toggle) { e.preventDefault(); toggleCollapse(t.toggle, e.altKey); return; }
      if (t.add && !locked) { e.preventDefault(); sel = [t.add]; addChild(t.add); return; }
      if (t.task && !locked) { e.preventDefault(); toggleTask(t.task); return; }
      if (t.link) { e.preventDefault(); openLink(t.link); return; }
      if (t.note) { e.preventDefault(); select([t.note]); editNote(t.note); return; }
      if (connectFrom) { endConnect(t.id || null); return; }
      if (t.rel) { selRel = t.rel; sel = []; drawContent(L); drawUI(); paintBar(); paintOutlineSel(); return; }
      if (t.id) {
        if (e.shiftKey || e.metaKey || e.ctrlKey) {
          sel = sel.includes(t.id) ? sel.filter((x) => x !== t.id) : [...sel, t.id];
          selRel = null; drawContent(L); drawUI(); paintBar(); paintOutlineSel();
          return;
        }
        if (!sel.includes(t.id) || selRel) { sel = [t.id]; if (selRel) { selRel = null; drawContent(L); } drawUI(); paintBar(); paintOutlineSel(); }
        else { sel = [...sel.filter((x) => x !== t.id), t.id]; }
        const b = L.boxes.get(t.id);
        drag = { kind: 'node', id: t.id, sx: e.clientX, sy: e.clientY, started: false, off: { x: p.x - b.x, y: p.y - b.y }, pointerId: e.pointerId };
        return;
      }
    }
    // empty canvas: pan (or draw a selection box with Shift, or always when Settings say so)
    const marquee = !panNow && e.button === 0 && (e.shiftKey || S().dragEmpty === 'select');
    drag = marquee ? { kind: 'marquee', sx: e.clientX, sy: e.clientY, a: p, b: p, started: false, base: e.shiftKey ? [...sel] : [] }
      : { kind: 'pan', sx: e.clientX, sy: e.clientY, vx: view.x, vy: view.y, started: false };
    drag.pointerId = e.pointerId;
  }
  // Capture the pointer only once a drag really starts: a capture from the first
  // press would retarget the click, and a double-click would miss the topic.
  function capture() { if (drag && !drag.captured) { drag.captured = true; try { canvas.setPointerCapture(drag.pointerId); } catch (_) { /* synthetic event */ } } }
  function onMove(e) {
    if (destroyed) return;
    if (e.pointerType === 'touch' && pts.has(e.pointerId)) {
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pinch && pts.size === 2) {
        const [a, b] = [...pts.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y); const cx = (a.x + b.x) / 2; const cy = (a.y + b.y) / 2;
        view.x += cx - pinch.cx; view.y += cy - pinch.cy;
        zoomAt(cx, cy, d / (pinch.d || d));
        pinch = { d, cx, cy };
        return;
      }
    }
    pointer = toMap(e.clientX, e.clientY);
    if (connectFrom) { drawUI(); return; }
    // the button was let go somewhere we never heard about: end the gesture now
    if (drag && e.pointerType === 'mouse' && e.buttons === 0) { onUp({ type: 'pointercancel', pointerType: 'mouse', pointerId: e.pointerId }); return; }
    if (!drag) { updateHover(e); return; }
    const dist = Math.hypot(e.clientX - drag.sx, e.clientY - drag.sy);
    if (!drag.started && dist < 4) return;
    capture();
    if (drag.kind === 'pan') {
      drag.started = true;
      view.x = drag.vx + e.clientX - drag.sx; view.y = drag.vy + e.clientY - drag.sy;
      canvas.classList.add('is-panning');
      applyView();
      return;
    }
    if (drag.kind === 'marquee') {
      drag.started = true; drag.b = pointer;
      const x0 = Math.min(drag.a.x, drag.b.x); const x1 = Math.max(drag.a.x, drag.b.x); const y0 = Math.min(drag.a.y, drag.b.y); const y1 = Math.max(drag.a.y, drag.b.y);
      const inside = [...L.boxes.values()].filter((b) => b.x < x1 && b.x + b.w > x0 && b.y < y1 && b.y + b.h > y0).map((b) => b.id);
      sel = [...new Set([...drag.base, ...inside])];
      drawUI(); paintBar();
      return;
    }
    if (drag.kind === 'node') {
      if (isBubble() && !locked) { dragBubble(e); return; }
      if (locked || drag.id === map.root.id || drag.id === focusId) {
        // the central topic (or a locked map) drags the whole view
        if (!drag.started) { drag.vx = view.x; drag.vy = view.y; }
        drag.started = true; drag.panning = true;
        view.x = drag.vx + e.clientX - drag.sx; view.y = drag.vy + e.clientY - drag.sy;
        applyView();
        return;
      }
      if (!drag.started) {
        drag.started = true;
        const branch = new Set();
        const mark = (nd) => { branch.add(nd.id); nd.children.forEach(mark); };
        mark(node(drag.id));
        drag.branch = branch;
        const b = L.boxes.get(drag.id);
        drag.ghost = { w: b.w, h: b.h, fs: b.st.fontSize, text: (b.nd.text || 'Topic').split('\n')[0].slice(0, 40), x: b.x, y: b.y };
        canvas.classList.add('is-dragging');
        drawContent(L);
      }
      drag.ghost.x = pointer.x - drag.off.x; drag.ghost.y = pointer.y - drag.off.y;
      drag.target = dropTarget(pointer);
      drag.copy = e.altKey;
      canvas.classList.toggle('is-copying', !!drag.copy);
      // near an edge: the canvas scrolls
      const r = rect(); const m = 36;
      let dx = 0; let dy = 0;
      if (e.clientX < r.left + m) dx = 14; else if (e.clientX > r.right - m) dx = -14;
      if (e.clientY < r.top + m) dy = 14; else if (e.clientY > r.bottom - m) dy = -14;
      if (dx || dy) { view.x += dx; view.y += dy; applyView(); }
      drawUI();
    }
  }
  /** A bubble map: the topic itself moves, its neighbours follow on their springs. */
  function dragBubble(e) {
    const b = L.boxes.get(drag.id);
    if (!b) return;
    if (!drag.started) {
      drag.started = true; drag.bubble = true;
      const branch = new Set();
      const mark = (nd) => { branch.add(nd.id); nd.children.forEach(mark); };
      mark(node(drag.id));
      drag.branch = branch;
      sim.dragId = drag.id;
      // while dragging, only the bubble's direct neighbours follow; the rest hold still, so a drop target stays put
      sim.near = new Set();
      for (const e2 of L.edges) { if (e2.from === drag.id) sim.near.add(e2.to); if (e2.to === drag.id) sim.near.add(e2.from); }
      for (const r2 of L.rels) { if (r2.from === drag.id) sim.near.add(r2.to); if (r2.to === drag.id) sim.near.add(r2.from); }
      canvas.classList.add('is-dragging');
      drawContent(L);
    }
    const p = sim.pos.get(drag.id);
    if (p) { p.x = pointer.x - drag.off.x + b.w / 2; p.y = pointer.y - drag.off.y + b.h / 2; p.vx = 0; p.vy = 0; }
    // over the middle of another bubble: dropping makes it a child there
    let target = null;
    for (const o of L.boxes.values()) {
      if (drag.branch.has(o.id)) continue;
      const r = o.w / 2; const d = Math.hypot(pointer.x - (o.x + r), pointer.y - (o.y + r));
      if (d < Math.max(r * 0.8, 12)) target = { kind: 'child', id: o.id };
    }
    if (target && idx.get(drag.id)?.parent?.id === target.id) target = null;
    drag.target = target;
    const r = rect(); const m = 36;
    let dx = 0; let dy = 0;
    if (e.clientX < r.left + m) dx = 14; else if (e.clientX > r.right - m) dx = -14;
    if (e.clientY < r.top + m) dy = 14; else if (e.clientY > r.bottom - m) dy = -14;
    if (dx || dy) { view.x += dx; view.y += dy; applyView(); }
    heat(0.3);
  }
  function dropBubble(d) {
    sim.dragId = null;
    const p = sim.pos.get(d.id);
    const t = d.target;
    if (t && M.canMoveUnder(map, idx, d.id, t.id)) {
      commit(() => { const ok = M.moveNode(map, idx, d.id, t.id, -1); const nd = node(d.id); if (ok && nd && p) { nd.gx = Math.round(p.x); nd.gy = Math.round(p.y); } return ok; }, { animate: false });
      toast(`Moved under "${(node(t.id)?.text || 'Topic').slice(0, 40)}".`, 1600);
    } else if (p) {
      commit(() => { const nd = node(d.id); if (!nd) return false; nd.gx = Math.round(p.x); nd.gy = Math.round(p.y); if (S().pinOnDrop !== false) nd.pin = true; }, { animate: false });
    }
    heat(0.3);
  }
  function onUp(e) {
    if (e.pointerType === 'touch') { pts.delete(e.pointerId); if (pts.size < 2) pinch = null; }
    const d = drag;
    drag = null;
    canvas.classList.remove('is-panning', 'is-dragging', 'is-copying');
    if (!d) return;
    if (d.kind === 'pan' && !d.started && e.type === 'pointerup') { if (sel.length || selRel) { sel = []; selRel = null; drawContent(L); drawUI(); paintBar(); paintOutlineSel(); } }
    if (d.kind === 'marquee') { drawUI(); paintOutlineSel(); }
    if (d.kind === 'node' && d.bubble) { if (e.type === 'pointerup') dropBubble(d); else { sim.dragId = null; heat(0.2); } }
    else if (d.kind === 'node' && d.started && !d.panning && e.type === 'pointerup') drop(d);
    else if (d.kind === 'node') { drawContent(L); drawUI(); }
  }
  function drop(d) {
    const p = pointer;
    const t = d.target;
    const id = d.id;
    const e0 = idx.get(id);
    if (!e0) return;
    let moved = false;
    const copy = d.copy;
    commit(() => {
      let nid = id;
      if (copy) {
        const c = M.cloneBranch(e0.node);
        // the copy starts as a floating topic, then goes where it was dropped
        c.x = 0; c.y = 0; map.floating.push(c); idx = M.indexMap(map); nid = c.id;
      }
      if (!t) {
        const pos = { x: p.x - d.off.x, y: p.y - d.off.y };
        if (!copy && M.isTop(map, id)) { const nd = node(id); nd.x = Math.round(pos.x); nd.y = Math.round(pos.y); moved = true; return true; }
        moved = M.moveNode(map, idx, nid, null, -1, pos);
        return moved || copy;
      }
      if (t.kind === 'child') {
        const side = t.id === map.root.id && map.layout === 'balanced' ? sideAt(p) : null;
        const tn = node(t.id);
        if (!copy && idx.get(id).parent?.id === t.id && !side) return false; // dropped back on its own parent
        moved = M.moveNode(map, idx, nid, t.id, tn.children.length, null, side);
        return moved;
      }
      const te = idx.get(t.id);
      const parent = te.parent;
      if (!parent) return copy;
      let at = parent.children.findIndex((x) => x.id === t.id);
      if (t.kind === 'after') at += 1;
      const side = parent === map.root && map.layout === 'balanced' ? (L.boxes.get(t.id)?.dir === 'left' ? 'left' : 'right') : null;
      moved = M.moveNode(map, idx, nid, parent.id, at, null, side);
      return moved;
    });
    drawUI();
    if (moved && copy) toast('Copied the branch there.', 1400);
  }
  let hoverRaf = 0;
  function updateHover(e) {
    const t = targetOf(e);
    let next = t.id || t.add || t.toggle || null;
    if (!next && hover && L.boxes.has(hover)) {
      // keep the hovered topic while the pointer crosses the gap to its + button
      const b = L.boxes.get(hover);
      const pad = 48 / view.k;
      const p = pointer;
      if (p.x > b.x - pad && p.x < b.x + b.w + pad && p.y > b.y - 12 / view.k && p.y < b.y + b.h + (b.dir === 'down' || b.dir === 'tree' ? pad : 12 / view.k)) next = hover;
    }
    if (next !== hover) {
      hover = next; cancelAnimationFrame(hoverRaf);
      hoverRaf = requestAnimationFrame(() => { if (isBubble() && !sim.raf && S().hoverHighlight !== false) drawContent(L); drawUI(); });
    }
    // a note's text shows when the pointer rests on its icon
    if (t.note) showNoteTip(e, t.note); else hideNoteTip();
  }
  const noteTip = h('div', { class: 'mm-notetip cthd-layer' });
  function showNoteTip(e, id) {
    const text = node(id)?.note;
    if (!text) return;
    if (!noteTip.isConnected) document.body.append(noteTip);
    noteTip.textContent = text.length > 600 ? `${text.slice(0, 600)}...` : text;
    noteTip.style.left = `${Math.min(innerWidth - 300, e.clientX + 14)}px`;
    noteTip.style.top = `${Math.min(innerHeight - 40, e.clientY + 14)}px`;
    noteTip.classList.add('is-on');
  }
  const hideNoteTip = () => noteTip.classList.remove('is-on');

  canvas.addEventListener('pointerdown', onDown);
  canvas.addEventListener('pointermove', onMove);
  canvas.addEventListener('pointerup', onUp);
  canvas.addEventListener('pointercancel', onUp);
  canvas.addEventListener('pointerleave', () => { if (!drag) { const had = hover; hover = null; if (had && isBubble() && !sim.raf) drawContent(L); drawUI(); hideNoteTip(); } if (opts.embedded && S().embedWheel !== 'always') engaged = false; });
  canvas.addEventListener('dblclick', (e) => {
    const t = targetOf(e);
    if (t.toggle || t.add || t.task || t.link || t.note) return;
    if (t.rel) { selRel = t.rel; editRelLabel(t.rel); return; }
    if (t.id) { startEdit(t.id); return; }
    if (!locked && S().dblClickCanvas !== false) addFloating(toMap(e.clientX, e.clientY));
  });
  canvas.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    drag = null;
    const t = targetOf(e);
    if (t.rel) { selRel = t.rel; sel = []; drawContent(L); drawUI(); paintBar(); relMenu(e); return; }
    if (t.id) { if (!sel.includes(t.id)) select([t.id]); topicMenu(e); return; }
    canvasMenu(e, toMap(e.clientX, e.clientY));
  });
  canvas.addEventListener('wheel', (e) => {
    if (opts.embedded && !engaged && S().embedWheel !== 'always') { showHint('Click the map to pan and zoom it.', 1800); return; } // the Notion page keeps scrolling
    e.preventDefault();
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? rect().height : 1;
    if (e.ctrlKey || e.metaKey || (S().wheel === 'zoom' && !e.shiftKey)) {
      zoomAt(e.clientX, e.clientY, Math.exp(-e.deltaY * unit * (e.ctrlKey ? 0.01 : 0.0022)));
    } else {
      view.x -= (e.shiftKey && !e.deltaX ? e.deltaY : e.deltaX) * unit;
      view.y -= (e.shiftKey && !e.deltaX ? 0 : e.deltaY) * unit;
      applyView();
    }
  }, { passive: false });
  // Safari trackpad pinch
  canvas.addEventListener('gesturestart', (e) => { e.preventDefault(); canvas.__g = 1; });
  canvas.addEventListener('gesturechange', (e) => { e.preventDefault(); zoomAt(e.clientX, e.clientY, e.scale / (canvas.__g || 1)); canvas.__g = e.scale; });

  // ---- menus --------------------------------------------------------------------------------
  function topicMenu(e) {
    const nds = selNodes(); const one = nds.length === 1 ? nds[0] : null;
    const isRoot = one && one.id === map.root.id;
    const top = one && M.isTop(map, one.id);
    const items = locked ? [
      one?.children.length ? { label: one.collapsed ? 'Expand' : 'Collapse', icon: one.collapsed ? 'chevrons-up-down' : 'chevrons-down-up', run: () => toggleCollapse() } : null,
      one ? { label: 'Focus on this branch', icon: 'focus', run: () => focusBranch() } : null,
      { label: 'Copy as text', icon: 'copy', run: () => copyAsText() },
      one?.note ? { label: 'Read the note', icon: 'sticky-note', run: () => editNote() } : null,
      one?.link ? { label: 'Open the link', icon: 'external-link', run: () => openLink(one.id) } : null,
    ] : [
      { label: 'Add child topic', hint: 'Tab', icon: 'corner-down-right', run: () => addChild() },
      one && !isRoot ? { label: 'Add sibling topic', hint: 'Enter', icon: 'list-plus', run: () => addSibling() } : null,
      one && !isRoot && !top ? { label: 'Add parent topic', hint: `${MOD}+Enter`, icon: 'between-horizontal-start', run: () => addParent() } : null,
      one ? { label: 'Edit text', hint: 'F2', icon: 'pencil', run: () => startEdit(one.id) } : null,
      'sep',
      one ? { label: one.note ? 'Edit note' : 'Add note', icon: 'sticky-note', run: () => editNote() } : null,
      one ? { label: one.link ? 'Edit link' : 'Add link', icon: 'link', run: () => editLink() } : null,
      { label: nds.every((nd) => nd.task) ? 'Remove checkbox' : 'Make it a task', icon: 'square-check-big', run: () => toggleTask() },
      one ? { label: 'Connect to another topic', icon: 'waypoints', run: () => startConnect() } : null,
      'sep',
      one?.children.length ? { label: one.collapsed ? 'Expand' : 'Collapse', hint: 'Space', icon: one.collapsed ? 'chevrons-up-down' : 'chevrons-down-up', run: () => toggleCollapse() } : null,
      one?.children.length ? { label: 'Expand the whole branch', icon: 'chevrons-up-down', run: () => { commit(() => M.setCollapsedDeep(one, false)); } } : null,
      one?.children.length ? { label: 'Collapse everything under it', icon: 'chevrons-down-up', run: () => { commit(() => { for (const c of one.children) M.setCollapsedDeep(c, true); }); } } : null,
      one ? { label: one.id === focusId ? 'Show the whole map' : 'Focus on this branch', icon: 'focus', run: () => (one.id === focusId ? exitFocus() : focusBranch()) } : null,
      isBubble() && one ? { label: 'Local graph (1 link away)', icon: 'share-2', run: () => showLocal(one.id, 1) } : null,
      isBubble() ? { label: nds.every((nd) => nd.pin) ? 'Unpin' : 'Pin where it is', icon: nds.every((nd) => nd.pin) ? 'pin-off' : 'pin', run: () => setPin(nds.map((nd) => nd.id), !nds.every((nd) => nd.pin)) } : null,
      'sep',
      { label: 'Copy', hint: `${MOD}+C`, icon: 'copy', run: () => { copySel(); toast('Copied.', 1000); } },
      { label: 'Copy as text outline', icon: 'file-text', run: () => copyAsText() },
      !isRoot ? { label: 'Duplicate', hint: `${MOD}+D`, icon: 'copy-plus', run: () => duplicate() } : null,
      one && !top ? { label: 'Detach as a floating topic', icon: 'circle-plus', run: () => detach() } : null,
      'sep',
      !nds.every((nd) => nd.id === map.root.id) ? { label: 'Delete', hint: 'Delete', icon: 'trash-2', danger: true, run: () => removeSelected() } : null,
    ];
    menu(e, tidy(items));
  }
  function relMenu(e) {
    menu(e, tidy(locked ? [] : [
      { label: 'Edit the label', icon: 'type', run: () => editRelLabel() },
      { label: 'Reverse the arrow', icon: 'arrow-left-right', run: () => relSet((x) => { const f = x.from; x.from = x.to; x.to = f; }) },
      { label: 'Straight or curved', icon: 'spline', run: () => relSet((x) => { x.bend = x.bend === 0 ? 0.25 : 0; }) },
      'sep',
      { label: 'Delete the relationship', icon: 'trash-2', danger: true, run: () => removeSelected() },
    ]));
  }
  function canvasMenu(e, p) {
    menu(e, tidy([
      !locked ? { label: 'Add a floating topic here', icon: 'circle-plus', run: () => addFloating(p) } : null,
      'sep',
      { label: 'Zoom to fit', hint: 'Shift+1', icon: 'maximize', run: () => fit() },
      { label: 'Centre the central topic', icon: 'locate-fixed', run: () => centerOn(focusId || map.root.id) },
      focusId ? { label: 'Show the whole map', icon: 'focus', run: () => exitFocus() } : null,
      local ? { label: 'Show the whole map', icon: 'share-2', run: () => exitLocal() } : null,
      isBubble() && !locked ? { label: 'Re-arrange the bubbles', icon: 'shuffle', run: () => rearrange() } : null,
      isBubble() && !locked ? { label: 'Unpin every topic', icon: 'pin-off', run: () => setPin([...M.walkAll(map)].filter((nd) => nd.pin).map((nd) => nd.id), false) } : null,
      'sep',
      { label: 'Expand everything', icon: 'chevrons-up-down', run: () => setAllCollapsed(false) },
      { label: 'Collapse to the main topics', icon: 'chevrons-down-up', run: () => setAllCollapsed(true) },
      'sep',
      { label: 'Select every topic', hint: `${MOD}+A`, icon: 'scan', run: () => select([...L.boxes.keys()]) },
      { label: 'Find', hint: `${MOD}+F`, icon: 'search', run: () => openFind() },
    ]));
  }
  const tidy = (items) => { const out = []; for (const it of items) { if (!it) continue; if (it === 'sep' && (!out.length || out[out.length - 1] === 'sep')) continue; out.push(it); } while (out[out.length - 1] === 'sep') out.pop(); return out; };
  function copyAsText() {
    const ids = topLevelSel();
    const text = ids.length ? ids.map((id) => mapToText(map, node(id))).join('\n') : mapToText(map);
    navigator.clipboard?.writeText(text).then(() => toast('Outline copied. Paste it into Notion or any note.', 2200), () => window.prompt('Copy this outline:', text));
  }

  // ---- keys ------------------------------------------------------------------------------
  let spaceDown = false;
  function nav(dir) {
    const id = primary();
    if (!id || !L.boxes.has(id)) { select([focusId || map.root.id]); return; }
    const b = L.boxes.get(id);
    const cx = b.x + b.w / 2; const cy = b.y + b.h / 2;
    let best = null; let bestScore = Infinity;
    for (const o of L.boxes.values()) {
      if (o.id === id) continue;
      const ox = o.x + o.w / 2; const oy = o.y + o.h / 2;
      const dx = ox - cx; const dy = oy - cy;
      let main; let cross;
      if (dir === 'right') { main = o.x - (b.x + b.w) >= -2 ? dx : -1; cross = Math.abs(dy); }
      else if (dir === 'left') { main = b.x - (o.x + o.w) >= -2 ? -dx : -1; cross = Math.abs(dy); }
      else if (dir === 'down') { main = o.y - (b.y + b.h) >= -2 ? dy : -1; cross = Math.abs(dx); }
      else { main = b.y - (o.y + o.h) >= -2 ? -dy : -1; cross = Math.abs(dx); }
      if (main <= 0) continue;
      const score = main + cross * 2.2;
      if (score < bestScore) { bestScore = score; best = o.id; }
    }
    if (!best) {
      // going out of a collapsed topic toward its branch opens it
      const nd = node(id);
      const grows = { right: ['right', 'both'], left: ['left', 'both'], down: ['down', 'tree'], up: ['up'] }[dir];
      if (nd?.collapsed && nd.children.length && grows.includes(b.dir)) { toggleCollapse(id); return; }
      return;
    }
    select([best]);
  }
  function onKey(e) {
    if (destroyed || editing) return;
    const tgt = e.target;
    if (tgt instanceof HTMLInputElement || tgt instanceof HTMLTextAreaElement || tgt?.isContentEditable) return;
    if (tgt?.closest?.('.mm-bar, .mm-zoom, .mm-find, .mm-crumbs') && (e.key === 'Enter' || e.key === ' ')) return; // let the button click
    const mod = e.metaKey || e.ctrlKey;
    const k = e.key;
    const done = () => { e.preventDefault(); e.stopPropagation(); };
    if (k === ' ' && !mod && !e.shiftKey && !primary()) { spaceDown = true; canvas.classList.add('is-space'); done(); return; }
    if (mod && !e.altKey) {
      const lk = k.toLowerCase();
      if (lk === 'z' && !e.shiftKey) { done(); undo(); return; }
      if ((lk === 'z' && e.shiftKey) || lk === 'y') { done(); redo(); return; }
      if (lk === 'f') { done(); openFind(); return; }
      if (lk === 'a') { done(); select([...L.boxes.keys()]); return; }
      if (lk === 'd') { done(); duplicate(); return; }
      if (lk === 'b') { done(); toggleFlag('bold'); return; }
      if (lk === 'i') { done(); toggleFlag('italic'); return; }
      if (lk === 'k') { done(); editLink(); return; }
      if (k === '0') { done(); fit(); return; }
      if (k === '=' || k === '+') { done(); zoomBy(1.25); return; }
      if (k === '-') { done(); zoomBy(1 / 1.25); return; }
      if (k === 'Enter') { done(); addParent(); return; }
      if (k === '/') { done(); toggleCollapse(); return; }
      if (k === '.') { done(); toggleCollapse(primary(), true); return; }
      return; // copy, cut, paste: the native events handle them
    }
    if (e.altKey && (k === 'ArrowUp' || k === 'ArrowLeft')) { done(); moveSel(-1); return; }
    if (e.altKey && (k === 'ArrowDown' || k === 'ArrowRight')) { done(); moveSel(1); return; }
    switch (k) {
      case 'Tab': done(); if (e.shiftKey) promote(); else addChild(); return;
      case 'Enter': done(); if (!primary()) { select([focusId || map.root.id]); return; } addSibling(e.shiftKey); return;
      case 'F2': done(); if (primary()) startEdit(primary()); return;
      case 'Delete': case 'Backspace': done(); removeSelected(); return;
      case 'Escape':
        done();
        if (connectFrom) { connectFrom = null; hideHint(); drawUI(); return; }
        if (pop) { closePop(); return; }
        if (findState) { closeFind(); return; }
        if (sel.length || selRel) { sel = []; selRel = null; drawContent(L); drawUI(); paintBar(); paintOutlineSel(); return; }
        if (local) { exitLocal(); return; }
        if (focusId) exitFocus();
        return;
      case 'ArrowRight': done(); nav('right'); return;
      case 'ArrowLeft': done(); nav('left'); return;
      case 'ArrowUp': done(); nav('up'); return;
      case 'ArrowDown': done(); nav('down'); return;
      case ' ': done(); toggleCollapse(primary(), e.altKey); return;
      case '!': if (e.shiftKey) { done(); fit(); } return;
      case '?': done(); opts.onShortcuts?.(); return;
      default:
    }
    if (k === ']' && primary()) { done(); demote(); return; }
    if (k === '[' && primary()) { done(); promote(); return; }
    // typing starts editing the selected topic, replacing its text
    if (k.length === 1 && !e.altKey && primary() && !locked) { done(); startEdit(primary(), { replace: k }); }
  }
  const onKeyUp = (e) => { if (e.key === ' ') { spaceDown = false; canvas.classList.remove('is-space'); } };
  wrap.addEventListener('keydown', onKey);
  wrap.addEventListener('keyup', onKeyUp);

  const ro = new ResizeObserver(() => { applyView(); drawMini(); requestAnimationFrame(placeBar); });
  ro.observe(canvas);

  paintZoom();

  // ---- the API the app uses -------------------------------------------------------------
  return {
    /** Show a map (a fresh open: history cleared, view fitted). */
    load(m, { fitView = true } = {}) {
      stopSim(); sim.pos.clear(); local = null;
      map = M.normalize(m);
      idx = M.indexMap(map);
      L = null;
      sel = []; selRel = null; focusId = null; editing = null; ta.hidden = true;
      undoStack.length = 0; redoStack.length = 0;
      ver = 0; savedVer = 0;
      relayout(false);
      if (fitView) requestAnimationFrame(() => fit());
      if (S().outline) setOutline(true);
    },
    /** The map as saved (a copy: later edits never change a save in flight). */
    state: () => JSON.parse(JSON.stringify(map)),
    title: () => map.root.text,
    version: () => ver,
    markSaved(v) { savedVer = Math.max(savedVer, v); },
    isDirty: () => ver !== savedVer || !!editing,
    isEditing: () => !!editing,
    /** Edit a topic's text now (its text selected, so typing replaces it). */
    editTopic: (id) => startEdit(id),
    finishEdit, undo, redo, fit, zoomBy, setZoom, centerOn, openFind, setLocked, isLocked: () => locked,
    setOutline, outlineOpen: () => outlineOpen, focusBranch, exitFocus,
    select: (ids) => select(ids),
    selection: () => [...sel],
    focusId: () => focusId,
    canUndo: () => undoStack.length > 0, canRedo: () => redoStack.length > 0,
    refresh() { relayout(false); applyView(); },
    /** Replace the whole map (from an outside reload) keeping the view. */
    replace(m) { map = M.normalize(m); sim.pos.clear(); undoStack.length = 0; redoStack.length = 0; relayout(false); },
    /** Bubble maps: true while the bubbles are still moving. */
    isSettling: () => !!sim.raf,
    focusCanvas: () => canvas.focus({ preventScroll: true }),
    view: () => ({ ...view }),
    destroy() {
      destroyed = true;
      cancelAnimationFrame(anim); stopSim(); clearTimeout(sim.persistT);
      ro.disconnect();
      window.removeEventListener('copy', onCopy);
      window.removeEventListener('cut', onCut);
      window.removeEventListener('paste', onPaste);
      closePop(); tip.remove(); noteTip.remove();
      wrap.remove();
    },
    measureText,
  };
}
